import React, { useState, useEffect } from 'react';
import { ChevronLeft, Edit3, Trash2, Download, Plus, Calendar, User, Mail, Briefcase, Hash, ChevronDown, ChevronUp, MoreVertical, X, Check, Save, DollarSign, Info, FileText, Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFlexiRequests } from '../../contexts/FlexiRequestContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import { api } from '../../services/api';
import { getProfessionalTax } from '../../config/payrollConfig';
import professionalTaxService from '../../services/professionalTax.service';
import pfService from '../../services/pf.service';
import tdsService from '../../services/tds.service';
import payrollService from '../../services/payroll.service';

const PayrollFlowWithEmployeeSelection = ({ preselectedEmployeeId, onBack }) => {
    const { theme } = useTheme();
    const { employees } = useEmployees();
    const {
        getPendingRequests,
        updateRequestStatus,
        getRequestsByEmployee,
        currentUser
    } = useFlexiRequests();
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // Auto-select employee if preselectedEmployeeId is provided
    useEffect(() => {
        if (preselectedEmployeeId && employees.length > 0) {
            const employee = employees.find(emp => emp.id === preselectedEmployeeId);
            if (employee) {
                setSelectedEmployee(employee);
            }
        }
    }, [preselectedEmployeeId, employees]);
    const [activeTab, setActiveTab] = useState('PAY PACKAGE');
    const [selectedYear, setSelectedYear] = useState('2023-24');
    const [availableYears, setAvailableYears] = useState(['2023-24', '2022-23', '2021-22']);
    const [showAddYearModal, setShowAddYearModal] = useState(false);
    const [newYear, setNewYear] = useState('');
    const [selectedRevisionId, setSelectedRevisionId] = useState('current'); // 'current' or revision id
    const [globalCurrency, setGlobalCurrency] = useState('INR');
    const [showFlexiStatusModal, setShowFlexiStatusModal] = useState(false);
    const [flexiStatus, setFlexiStatus] = useState({
        status: 'OPEN',
        fromDate: '2023-04-01',
        upToDate: '2023-06-30'
    });
    const [isFlexiEditable, setIsFlexiEditable] = useState(false);
    const [flexiValues, setFlexiValues] = useState({
        mobileAllowance: 25000,
        fuelAllowance: 30000
    });
    const [showFlexiHistoryModal, setShowFlexiHistoryModal] = useState(false);
    const [selectedFlexiComponent, setSelectedFlexiComponent] = useState(null);
    const [flexiComponentStatuses, setFlexiComponentStatuses] = useState({
        mobileAllowance: { status: 'OPEN', fromDate: '2023-04-01', upToDate: '2023-06-30' },
        fuelAllowance: { status: 'OPEN', fromDate: '2023-04-01', upToDate: '2023-06-30' }
    });
    const [flexiHistory, setFlexiHistory] = useState({
        mobileAllowance: [
            { status: 'OPEN', fromDate: '2023-04-01', toDate: '2023-06-30', changedOn: '2023-04-01', changedBy: 'Admin User' },
            { status: 'CLOSED', fromDate: '2023-02-01', toDate: '2023-03-31', changedOn: '2023-02-01', changedBy: 'HR Manager' }
        ],
        fuelAllowance: [
            { status: 'OPEN', fromDate: '2023-04-01', toDate: '2023-06-30', changedOn: '2023-04-01', changedBy: 'Admin User' },
            { status: 'Pending', fromDate: '2023-01-01', toDate: 'NA', changedOn: '2023-01-01', changedBy: 'Admin User' }
        ]
    });
    const [expandedYears, setExpandedYears] = useState({});
    const [showAddFlexiModal, setShowAddFlexiModal] = useState(false);
    const [isEditingFlexiComponents, setIsEditingFlexiComponents] = useState(false);
    const [newFlexiComponent, setNewFlexiComponent] = useState({
        name: '',
        lowerLimit: '',
        upperLimit: '',
        value: 0
    });
    const [customFlexiComponents, setCustomFlexiComponents] = useState([]);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [requestAction, setRequestAction] = useState('');
    const [dismissReason, setDismissReason] = useState('');

    // States for modals
    const [showAddVariablePayModal, setShowAddVariablePayModal] = useState(false);
    const [showCTCProrationModal, setShowCTCProrationModal] = useState(false);
    const [selectedProrationRow, setSelectedProrationRow] = useState(null);
    const [editingPayPackage, setEditingPayPackage] = useState(null);
    const [payPackageData, setPayPackageData] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [editingRow, setEditingRow] = useState(null);

    // Salary Components states
    const [salaryComponents, setSalaryComponents] = useState([
        { name: 'Basic Salary', amount: 600000, monthly_amount: 50000, type: 'earning', is_custom: false, category: 'fixed' },
        { name: 'HRA', amount: 240000, monthly_amount: 20000, type: 'earning', is_custom: false, category: 'allowance' },
        { name: 'Special Allowance', amount: 120000, monthly_amount: 10000, type: 'earning', is_custom: false, category: 'allowance' },
        { name: 'Variable Pay', amount: 240000, monthly_amount: 20000, type: 'earning', is_custom: false, category: 'variable' }
    ]);
    const [viewMode, setViewMode] = useState('annual'); // 'annual' or 'monthly'
    const [showComponentSelector, setShowComponentSelector] = useState(false);
    const [customComponentName, setCustomComponentName] = useState('');
    const [componentType, setComponentType] = useState('earning');
    const [isEditingComponents, setIsEditingComponents] = useState(false);
    const [loadingPayGroup, setLoadingPayGroup] = useState(false);
    const [payGroupStatus, setPayGroupStatus] = useState(null); // null, 'found', 'not_configured'
    // FIX: Store calculated statutory deductions separately to prevent race condition
    // When revision auto-selects, it won't overwrite these calculated values
    const [calculatedStatutoryComponents, setCalculatedStatutoryComponents] = useState([]);
    // FIX: Company config for PF/PT settings (fetched on mount) - same pattern as PayrollRun.js
    const [companyConfig, setCompanyConfig] = useState({ is_pf_applicable: true, is_tds_applicable: true });
    const [companyConfigLoaded, setCompanyConfigLoaded] = useState(false); // Wait for config before PF calc

    // Pay slips states - initialized empty, will be populated from API
    const [paySlipsData, setPaySlipsData] = useState([]);
    const [selectedPaySlips, setSelectedPaySlips] = useState([]);
    const [showPaySlipCheckboxes, setShowPaySlipCheckboxes] = useState(false);
    const [paySlipsLoading, setPaySlipsLoading] = useState(false);
    const [paySlipsError, setPaySlipsError] = useState(null);
    const [regeneratingPayslipId, setRegeneratingPayslipId] = useState(null);

    // Salary Revision states
    const [salaryRevisions, setSalaryRevisions] = useState([]);
    const [showRevisionModal, setShowRevisionModal] = useState(false);
    const [revisionRefreshKey, setRevisionRefreshKey] = useState(0); // Used to force re-fetch salary revisions
    const [revisionFormData, setRevisionFormData] = useState({
        revision_date: new Date().toISOString().split('T')[0],
        effective_from: '',
        revision_type: 'annual_increment',
        new_annual_ctc: '',
        reason: '',
        remarks: ''
    });
    const [editingRevision, setEditingRevision] = useState(null); // Holds revision being edited
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // Holds revision ID for delete confirmation
    const [revisionActionLoading, setRevisionActionLoading] = useState(false);

    // Bonus Allocation states
    const [bonusAllocations, setBonusAllocations] = useState([]);
    const [bonusLoading, setBonusLoading] = useState(false);
    const [showBonusModal, setShowBonusModal] = useState(false);
    const [editingBonus, setEditingBonus] = useState(null);
    const [bonusFormData, setBonusFormData] = useState({
        bonusType: 'Performance Bonus',
        amount: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        reason: ''
    });
    const [bonusActionLoading, setBonusActionLoading] = useState(false);
    const [bonusRefreshKey, setBonusRefreshKey] = useState(0);
    const [processedPayrollPeriods, setProcessedPayrollPeriods] = useState(new Set());

    // FIX: Fetch company PF/TDS configuration AND company state on mount (same pattern as PayrollRun.js)
    useEffect(() => {
        const fetchCompanyConfig = async () => {
            try {
                const companyId = 'b52eee49-1e89-414d-9e51-f2747549ad08';

                // Fetch PF configuration
                const pfResponse = await api.get(`/api/pf/configuration/${companyId}`);
                if (pfResponse.data.success) {
                    setCompanyConfig(prev => ({
                        ...prev,
                        is_pf_applicable: pfResponse.data.data?.is_pf_applicable ?? true
                    }));
                    console.log('📋 Company PF config loaded:', pfResponse.data.data?.is_pf_applicable);
                }

                // FIX: Fetch company settings to get state for PT calculation
                try {
                    const settingsResponse = await api.get('/api/company/settings');
                    if (settingsResponse.data) {
                        // Extract state from company settings (address.state)
                        const settings = settingsResponse.data.data || settingsResponse.data;
                        const companyState = settings?.address?.state || settings?.state || 'Maharashtra';
                        setCompanyConfig(prev => ({
                            ...prev,
                            state: companyState
                        }));
                        console.log('📋 Company state loaded for PT:', companyState);
                    }
                } catch (settingsError) {
                    console.warn('Could not fetch company settings, using default state:', settingsError.message);
                    setCompanyConfig(prev => ({
                        ...prev,
                        state: 'Maharashtra'
                    }));
                }
            } catch (error) {
                console.warn('Could not fetch company PF config, using defaults:', error.message);
            } finally {
                setCompanyConfigLoaded(true); // Mark config as loaded (even on error, use defaults)
            }
        };
        fetchCompanyConfig();
    }, []);

    // Fetch salary revisions when employee is selected or refresh key changes
    useEffect(() => {
        const fetchSalaryRevisions = async () => {
            if (selectedEmployee && selectedEmployee.id) {
                try {
                    console.log('📈 Fetching salary revisions for employee:', selectedEmployee.id);
                    const response = await api.get(`/api/salary-revisions/employees/${selectedEmployee.id}/revisions`);
                    if (response.data && response.data.revisions) {
                        console.log('📈 Received revisions:', response.data.revisions.length, response.data.revisions.map(r => ({
                            id: r.id,
                            status: r.payroll_confirmation_status,
                            ctc: r.new_annual_ctc
                        })));
                        setSalaryRevisions(response.data.revisions);
                    }
                } catch (error) {
                    console.error('Error fetching salary revisions:', error);
                    // Set empty array if table doesn't exist yet or other error
                    setSalaryRevisions([]);
                }
            }
        };
        fetchSalaryRevisions();
    }, [selectedEmployee, revisionRefreshKey]);

    // Fetch bonus allocations when employee is selected
    useEffect(() => {
        const fetchBonusAllocations = async () => {
            if (selectedEmployee && selectedEmployee.id) {
                setBonusLoading(true);
                try {
                    const response = await api.get(`/api/compensation/employees/${selectedEmployee.id}/bonus-allocations`);
                    if (response.data && response.data.bonuses) {
                        setBonusAllocations(response.data.bonuses);

                        // Check which payroll periods are processed
                        const uniquePeriods = [...new Set(
                            response.data.bonuses.map(b => `${b.month}-${b.year}`)
                        )];
                        const processed = new Set();
                        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                        await Promise.all(uniquePeriods.map(async (periodKey) => {
                            const [m, y] = periodKey.split('-');
                            const periodStr = `${monthNames[parseInt(m) - 1]} ${y}`;
                            try {
                                const resp = await payrollService.getPayrollStatus(periodStr);
                                const s = resp.status || resp;
                                console.log(`🎁 Payroll status for ${periodStr}:`, s);
                                if (s.processed || s.locked || s.status === 'completed' || s.status === 'processed') {
                                    processed.add(periodKey);
                                    console.log(`🎁 Marked ${periodStr} as processed`);
                                }
                            } catch (e) { console.warn(`🎁 Payroll status check failed for ${periodStr}:`, e.message); }
                        }));
                        console.log('🎁 Final processed periods:', [...processed]);
                        setProcessedPayrollPeriods(processed);
                    }
                } catch (error) {
                    console.error('Error fetching bonus allocations:', error);
                    setBonusAllocations([]);
                } finally {
                    setBonusLoading(false);
                }
            }
        };
        fetchBonusAllocations();
    }, [selectedEmployee, bonusRefreshKey]);

    // Refresh salary revisions when tab becomes visible (in case it was updated in PayrollRun)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && selectedEmployee?.id) {
                console.log('📈 Tab became visible, refreshing salary revisions');
                setRevisionRefreshKey(prev => prev + 1);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [selectedEmployee]);

    // FIX: Helper function to filter out statutory deductions from stored components
    // These will be recalculated based on org configuration (PF enabled/disabled, PT slabs, etc.)
    const filterStatutoryDeductions = (components) => {
        if (!components || !Array.isArray(components)) return [];
        return components.filter(comp => {
            const name = (comp.name || '').toLowerCase();
            const isStatutory =
                name.includes('provident fund') || name.includes(' pf') || name === 'pf' ||
                name.includes('professional tax') ||
                name.includes('income tax') || name.includes('tds');
            return !isStatutory;
        });
    };

    // FIX: Merge non-statutory (from revision/DB) with statutory (from calculation)
    // This prevents race condition where revision load overwrites calculated statutory
    const allSalaryComponents = React.useMemo(() => {
        // Filter out any statutory from salaryComponents (safety net)
        const nonStatutory = filterStatutoryDeductions(salaryComponents);
        // Merge with calculated statutory components (PF, PT, TDS from backend APIs)
        return [...nonStatutory, ...calculatedStatutoryComponents];
    }, [salaryComponents, calculatedStatutoryComponents]);

    // Build revision options list from salary revisions
    // Options: "Current Salary" + all historical revisions sorted by effective date (newest first)
    const revisionOptions = React.useMemo(() => {
        const options = [
            { id: 'current', label: 'Current Salary', effectiveFrom: null, ctc: null }
        ];

        if (salaryRevisions && salaryRevisions.length > 0) {
            // Sort revisions by effective_from date (newest first)
            const sortedRevisions = [...salaryRevisions].sort((a, b) => {
                const dateA = new Date(a.effective_from);
                const dateB = new Date(b.effective_from);
                return dateB - dateA;
            });

            sortedRevisions.forEach(rev => {
                const effectiveDate = new Date(rev.effective_from);
                const formattedDate = effectiveDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
                const revisionType = (rev.revision_type || 'revision').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                const statusLabel = rev.payroll_confirmation_status === 'confirmed' ? ' ✓' : '';

                options.push({
                    id: rev.id,
                    label: `${revisionType} - ${formattedDate}${statusLabel}`,
                    effectiveFrom: rev.effective_from,
                    ctc: rev.new_annual_ctc,
                    components: rev.new_salary_components,
                    oldCtc: rev.old_annual_ctc,
                    oldComponents: rev.old_salary_components,
                    status: rev.payroll_confirmation_status
                });
            });
        }

        return options;
    }, [salaryRevisions]);

    // Handle revision selection - update salary components based on selection
    const handleRevisionSelect = async (revisionId) => {
        setSelectedRevisionId(revisionId);

        if (revisionId === 'current') {
            // Load current salary from employee's pay_package
            if (selectedEmployee && selectedEmployee.id) {
                try {
                    const response = await api.get(`/api/admin/employees/${selectedEmployee.id}`);
                    if (response.data && response.data.employee) {
                        const empData = response.data.employee;
                        const payPkg = empData.pay_package;

                        if (payPkg && payPkg.salary_components && Array.isArray(payPkg.salary_components)) {
                            console.log('📈 Loading current salary components:', payPkg.salary_components.length);
                            // FIX: Filter out statutory deductions - they will be recalculated
                            setSalaryComponents(filterStatutoryDeductions(payPkg.salary_components));
                        } else if (empData.salary_components && Array.isArray(empData.salary_components)) {
                            // FIX: Filter out statutory deductions
                            setSalaryComponents(filterStatutoryDeductions(empData.salary_components));
                        } else {
                            setSalaryComponents([]);
                        }
                    }
                } catch (error) {
                    console.error('Error loading current salary:', error);
                }
            }
        } else {
            // Load salary components from the selected revision
            const selectedRevision = salaryRevisions.find(rev => rev.id === revisionId);
            console.log('📈 Selected revision:', selectedRevision);
            console.log('📈 new_salary_components type:', typeof selectedRevision?.new_salary_components);
            console.log('📈 new_salary_components value:', selectedRevision?.new_salary_components);
            console.log('📈 new_annual_ctc:', selectedRevision?.new_annual_ctc);

            if (selectedRevision && selectedRevision.new_salary_components) {
                let components = selectedRevision.new_salary_components;
                // Parse if it's a string
                if (typeof components === 'string') {
                    try {
                        components = JSON.parse(components);
                    } catch (e) {
                        console.error('Error parsing salary components:', e);
                        components = [];
                    }
                }

                // Ensure components is an array
                if (!Array.isArray(components)) {
                    console.error('📈 Components is not an array:', components);
                    components = [];
                }

                console.log('📈 Loading revision salary components:', components?.length, components);

                // Ensure each component has the required fields
                const normalizedComponents = components.map(comp => ({
                    name: comp.name || comp.component_name || 'Unknown',
                    amount: parseFloat(comp.amount || comp.annual_amount || 0),
                    monthly_amount: parseFloat(comp.monthly_amount || (comp.amount ? comp.amount / 12 : 0)),
                    type: comp.type || 'earning',
                    is_custom: comp.is_custom || false,
                    category: comp.category || 'fixed'
                }));

                console.log('📈 Normalized components:', normalizedComponents);
                // FIX: Filter out statutory deductions - they will be recalculated
                setSalaryComponents(filterStatutoryDeductions(normalizedComponents));
            } else {
                console.log('📈 No salary components found in revision');
                setSalaryComponents([]);
            }
        }
    };

    // Auto-select the latest revision when revisions are loaded
    useEffect(() => {
        if (salaryRevisions && salaryRevisions.length > 0) {
            // Find the latest confirmed revision, or just the latest revision
            const confirmedRevisions = salaryRevisions.filter(rev => rev.payroll_confirmation_status === 'confirmed');
            const sortedRevisions = [...(confirmedRevisions.length > 0 ? confirmedRevisions : salaryRevisions)].sort((a, b) => {
                const dateA = new Date(a.effective_from);
                const dateB = new Date(b.effective_from);
                return dateB - dateA;
            });

            if (sortedRevisions.length > 0) {
                // Auto-select the latest confirmed revision
                const latestRevision = sortedRevisions[0];
                console.log('📈 Auto-selecting latest revision:', latestRevision.id, latestRevision.effective_from);
                handleRevisionSelect(latestRevision.id);
            }
        } else {
            // No revisions - select current
            setSelectedRevisionId('current');
        }
    }, [salaryRevisions]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch employee's pay group and salary components
    useEffect(() => {
        const fetchEmployeePayGroup = async () => {
            // FIX: Wait for company config to be loaded before calculating PF
            if (!companyConfigLoaded) {
                console.log('⏳ Waiting for company config to load before fetching pay group...');
                return;
            }
            if (selectedEmployee?.id) {
                setLoadingPayGroup(true);
                setPayGroupStatus(null);
                try {
                    console.log('🔍 Fetching pay group for employee:', selectedEmployee.id);
                    const response = await api.get(`/api/pay-groups/employee/${selectedEmployee.id}`);

                    if (response.data.success && response.data.payGroup) {
                        const payGroup = response.data.payGroup;
                        console.log('✅ Received pay group:', payGroup.name);
                        console.log('📊 Salary components:', payGroup.salary_components);

                        // Transform backend components to frontend format
                        if (payGroup.salary_components && Array.isArray(payGroup.salary_components)) {
                            const transformedComponents = payGroup.salary_components.map(comp => ({
                                name: comp.component_name || comp.name,
                                amount: comp.amount || 0,
                                monthly_amount: comp.monthly_amount || 0,
                                type: comp.component_type || comp.type || 'earning',
                                is_custom: false,
                                category: comp.category || 'fixed'
                            }));

                            console.log('💰 Processing salary components from pay group');
                            const annualCTC = parseFloat(
                                selectedEmployee?.ctc ||
                                selectedEmployee?.pay_package?.annual_ctc ||
                                selectedEmployee?.pay_package?.ctc ||
                                payGroup.max_ctc ||
                                payGroup.min_ctc ||
                                0
                            ) || 0;

                            // First, calculate amounts for percentage-based components
                            const componentsWithAmounts = transformedComponents.map(comp => {
                                if (comp.amount) return comp;
                                const pct = parseFloat(comp.percentage || comp.component_percentage || comp.component_percent || 0);
                                if (!annualCTC || !pct) return comp;
                                const annual = Math.round((annualCTC * pct / 100) * 100) / 100;
                                return {
                                    ...comp,
                                    amount: annual,
                                    monthly_amount: Math.round((annual / 12) * 100) / 100
                                };
                            });

                            // Calculate monthly gross from earnings to determine PT
                            const monthlyGross = componentsWithAmounts
                                .filter(comp => comp.type === 'earning')
                                .reduce((sum, comp) => {
                                    const monthly = comp.monthly_amount ?? (comp.amount ? comp.amount / 12 : 0);
                                    return sum + parseFloat(monthly || 0);
                                }, 0);

                            // Calculate Professional Tax using backend API (with fallback to frontend)
                            // FIX: Use company/organization address state (not employee state) for PT calculation
                            let ptMonthly = 0;
                            let ptSource = 'unknown';

                            try {
                                // Get current month in YYYY-MM format
                                const now = new Date();
                                const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

                                // FIX: Use company state from companyConfig (fetched from company settings)
                                const companyState = companyConfig.state || 'Maharashtra';

                                // Call backend PT API with company state
                                if (selectedEmployee?.id && monthlyGross > 0) {
                                    const ptResult = await professionalTaxService.calculatePT(
                                        selectedEmployee.id,
                                        monthlyGross,
                                        currentMonth,
                                        companyState  // FIX: Pass company state to override employee state lookup
                                    );
                                    ptMonthly = ptResult.ptAmount || 0;
                                    ptSource = ptResult.source || 'backend';
                                    console.log(`✅ PT from ${ptSource}: Employee ${selectedEmployee.employee_code}, Company State=${companyState}, Gross ₹${monthlyGross.toFixed(2)}, PT ₹${ptMonthly}/month`);
                                } else {
                                    // Fallback to sync calculation with company state
                                    ptMonthly = getProfessionalTax(companyState, monthlyGross);
                                    ptSource = 'frontend-sync';
                                    console.log(`⚠️ PT fallback: Company State=${companyState}, Gross=₹${monthlyGross.toFixed(2)}, PT=₹${ptMonthly}/month`);
                                }
                            } catch (error) {
                                console.error('PT calculation error, using frontend fallback:', error);
                                // FIX: Use company state for fallback calculation
                                const companyState = companyConfig.state || 'Maharashtra';
                                ptMonthly = getProfessionalTax(companyState, monthlyGross);
                                ptSource = 'frontend-fallback';
                            }

                            const ptAnnual = parseFloat((ptMonthly * 12).toFixed(2));

                            // Only add PT component if amount > 0
                            const professionalTax = ptMonthly > 0 ? {
                                name: 'Professional Tax',
                                amount: ptAnnual,
                                monthly_amount: parseFloat(ptMonthly.toFixed(2)),
                                type: 'deduction',
                                is_custom: false,
                                category: 'statutory',
                                auto_calculated: true,  // Flag to indicate this is auto-calculated
                                pt_source: ptSource  // Track PT source for debugging
                            } : null;

                            // Calculate Provident Fund (PF) using backend API (with fallback)
                            let pfMonthly = 0;
                            let pfSource = 'unknown';
                            let pfExempted = false;

                            // FIX: Check company config first - skip PF if not applicable (same as PayrollRun.js)
                            if (!companyConfig.is_pf_applicable) {
                                console.log('ℹ️ PF disabled at company level, skipping calculation');
                                pfMonthly = 0;
                                pfExempted = true;
                                pfSource = 'company-disabled';
                            } else {
                                try {
                                    // Extract Basic and DA from salary components
                                    const basicComp = componentsWithAmounts.find(c =>
                                        c.name.toLowerCase().includes('basic')
                                    );
                                    const daComp = componentsWithAmounts.find(c =>
                                        c.name.toLowerCase().includes('da') ||
                                        c.name.toLowerCase().includes('dearness')
                                    );

                                    const basicSalary = basicComp?.monthly_amount || (basicComp?.amount ? basicComp.amount / 12 : 0);
                                    const daSalary = daComp?.monthly_amount || (daComp?.amount ? daComp.amount / 12 : 0);

                                    // Get current month in YYYY-MM format
                                    const now = new Date();
                                    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

                                    // Call backend PF API if we have basic salary
                                    if (selectedEmployee?.id && basicSalary > 0) {
                                        console.log(`🔍 Calling PF API for Employee ${selectedEmployee.employee_code}...`);
                                        const pfResult = await pfService.calculatePF(
                                            selectedEmployee.id,
                                            basicSalary,
                                            daSalary,
                                            currentMonth
                                        );

                                        // Debug: Log full API response
                                        console.log('📦 PF API Response:', pfResult);

                                        pfMonthly = pfResult.pfAmount || 0;
                                        pfSource = pfResult.source || 'backend';
                                        pfExempted = pfResult.exemptionApplied || false;

                                        // Log PF calculation result with exemption status
                                        if (pfResult.exemptionApplied) {
                                            console.log(`ℹ️ PF Exempted: Employee ${selectedEmployee.employee_code}, Reason: ${pfResult.exemptionReason || 'Not specified'}`);
                                        } else if (pfMonthly > 0) {
                                            console.log(`✅ PF from ${pfSource}: Employee ${selectedEmployee.employee_code}, Basic ₹${basicSalary.toFixed(2)}, DA ₹${daSalary.toFixed(2)}, PF ₹${pfMonthly}/month`);
                                        } else {
                                            console.log(`⚠️ PF calculated as ₹0: Employee ${selectedEmployee.employee_code}`);
                                        }

                                        // Debug: Log exemption flag
                                        console.log(`🔍 PF Exemption Check: pfExempted=${pfExempted}, pfMonthly=${pfMonthly}`);
                                    }
                                } catch (error) {
                                    console.error('❌ PF calculation error - backend API failed:', error);
                                    // Don't fallback to calculation - we need backend to check if PF is enabled
                                    // Setting PF to 0 is safer than calculating blindly
                                    pfMonthly = 0;
                                    pfSource = 'error-skip';
                                    console.warn('⚠️ PF set to ₹0 due to API error. Backend check required for PF configuration.');
                                }
                            }

                            const pfAnnual = parseFloat((pfMonthly * 12).toFixed(2));

                            // FIX: Simplified check - existing PF is now filtered out in nonStatutoryComponents
                            // Only add PF component if amount > 0 and not exempted
                            const providentFund = (pfMonthly > 0 && !pfExempted) ? {
                                name: 'Provident Fund (PF)',
                                amount: pfAnnual,
                                monthly_amount: parseFloat(pfMonthly.toFixed(2)),
                                type: 'deduction',
                                is_custom: false,
                                category: 'statutory',
                                auto_calculated: true,  // Flag to indicate this is auto-calculated
                                pf_source: pfSource  // Track PF source for debugging
                            } : null;

                            // Calculate Income Tax (TDS) using backend API (with fallback)
                            let tdsMonthly = 0;
                            let tdsSource = 'unknown';
                            let tdsExempted = false;

                            try {
                                // Calculate annual gross from all earnings components
                                const annualGross = componentsWithAmounts
                                    .filter(c => c.type === 'earning' || !c.type)
                                    .reduce((sum, c) => sum + (c.amount || 0), 0);

                                // Get current financial year
                                const now = new Date();
                                const currentFY = tdsService.getCurrentFinancialYear();

                                // Call backend TDS API if we have annual gross
                                if (selectedEmployee?.id && annualGross > 0) {
                                    const tdsResult = await tdsService.calculateTDS(
                                        selectedEmployee.id,
                                        annualGross,
                                        currentFY
                                    );
                                    tdsMonthly = tdsResult.tdsAmount || 0;
                                    tdsSource = tdsResult.source || 'backend';
                                    tdsExempted = tdsResult.exemptionApplied || false;

                                    // Log TDS calculation result with exemption status
                                    if (tdsResult.exemptionApplied) {
                                        console.log(`ℹ️ TDS Exempted: Employee ${selectedEmployee.employee_code}, Reason: ${tdsResult.exemptionReason || 'Not specified'}`);
                                    } else if (tdsMonthly > 0) {
                                        console.log(`✅ TDS from ${tdsSource}: Employee ${selectedEmployee.employee_code}, Annual Gross ₹${annualGross.toFixed(2)}, Regime: ${tdsResult.taxRegime}, TDS ₹${tdsMonthly}/month`);
                                    } else {
                                        console.log(`⚠️ TDS calculated as ₹0: Employee ${selectedEmployee.employee_code}, Annual Gross ₹${annualGross.toFixed(2)}`);
                                    }
                                }
                            } catch (error) {
                                console.error('❌ TDS calculation error - backend API failed:', error);
                                // Don't fallback to calculation - we need backend to check if TDS is enabled
                                // Setting TDS to 0 is safer than calculating blindly
                                tdsMonthly = 0;
                                tdsSource = 'error-skip';
                                console.warn('⚠️ TDS set to ₹0 due to API error. Backend check required for TDS configuration.');
                            }

                            const tdsAnnual = parseFloat((tdsMonthly * 12).toFixed(2));

                            // FIX: Simplified check - existing TDS is now filtered out in nonStatutoryComponents
                            // Only add TDS component if amount > 0 and not exempted
                            const incomeTax = (tdsMonthly > 0 && !tdsExempted) ? {
                                name: 'Income Tax (TDS)',
                                amount: tdsAnnual,
                                monthly_amount: parseFloat(tdsMonthly.toFixed(2)),
                                type: 'deduction',
                                is_custom: false,
                                category: 'statutory',
                                auto_calculated: true,  // Flag to indicate this is auto-calculated
                                tds_source: tdsSource  // Track TDS source for debugging
                            } : null;

                            // FIX: Filter out existing statutory deductions from pay group
                            // This ensures we only use freshly calculated values (which respect org-level config)
                            const nonStatutoryComponents = componentsWithAmounts.filter(comp => {
                                const name = (comp.name || '').toLowerCase();
                                const isStatutory =
                                    name.includes('provident fund') || name.includes(' pf') || name === 'pf' ||
                                    name.includes('professional tax') ||
                                    name.includes('income tax') || name.includes('tds');
                                return !isStatutory;
                            });

                            // FIX: Store calculated statutory components separately (prevents race condition)
                            // When revision auto-selects, it won't overwrite these calculated values
                            const statutoryOnly = [professionalTax, providentFund, incomeTax].filter(Boolean);
                            setCalculatedStatutoryComponents(statutoryOnly);
                            console.log('✅ Stored statutory components:', statutoryOnly.map(c => c.name));

                            // Only set non-statutory to salaryComponents
                            // The allSalaryComponents useMemo will merge with calculatedStatutoryComponents
                            setSalaryComponents(nonStatutoryComponents);
                            setPayGroupStatus('found');
                        } else {
                            setPayGroupStatus('not_configured');
                        }
                    } else {
                        console.log('⚠️ No pay group found for employee');
                        setPayGroupStatus('not_configured');
                    }
                } catch (error) {
                    console.error('❌ Error fetching employee pay group:', error);
                    setPayGroupStatus('not_configured');
                } finally {
                    setLoadingPayGroup(false);
                }
            }
        };

        fetchEmployeePayGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEmployee, companyConfigLoaded, companyConfig.is_pf_applicable, companyConfig.state]); // Re-run when config loaded, PF/PT settings change

    // Fetch payslips for the selected employee
    useEffect(() => {
        const fetchEmployeePayslips = async () => {
            if (!selectedEmployee?.id) {
                setPaySlipsData([]);
                return;
            }

            setPaySlipsLoading(true);
            setPaySlipsError(null);

            try {
                console.log('📋 Fetching payslips for employee:', selectedEmployee.id);

                // Use the API to get payslips for this employee
                const response = await api.get(`/api/payslips/employee/${selectedEmployee.id}`);

                if (response.data.success && response.data.payslips) {
                    // Transform backend data to match frontend table structure
                    const transformedPayslips = response.data.payslips.map(payslip => {
                        // Parse salary breakdown for TDS
                        const deductionsBreakdown = payslip.salary_breakdown?.deductions || [];
                        const tdsDeduction = deductionsBreakdown.find(d =>
                            d.name?.toLowerCase().includes('tds') ||
                            d.name?.toLowerCase().includes('income tax')
                        );

                        return {
                            id: payslip.id,
                            month: payslip.period_name || `${getMonthName(payslip.month)} ${payslip.year}`,
                            totalWorkDays: payslip.total_working_days || 0,
                            gross: payslip.gross_salary || 0,
                            deductions: payslip.total_deductions || 0,
                            tds: tdsDeduction?.amount || 0,
                            net: payslip.net_salary || 0,
                            status: payslip.status === 'locked' ? 'Released' : payslip.status || 'Pending',
                            pdfUrl: payslip.pdf_url,
                            firebasePath: payslip.firebase_path,
                            fileName: payslip.file_name,
                            year: payslip.year,
                            monthNum: payslip.month,
                            salaryBreakdown: payslip.salary_breakdown || { earnings: [], deductions: [] },
                            daysWorked: payslip.days_worked || 0,
                            lopDays: payslip.lop_days || 0
                        };
                    });

                    console.log(`✅ Found ${transformedPayslips.length} payslips for employee`);
                    setPaySlipsData(transformedPayslips);
                } else {
                    console.log('ℹ️ No payslips found for employee');
                    setPaySlipsData([]);
                }
            } catch (error) {
                console.error('❌ Error fetching payslips:', error);
                setPaySlipsError(error.message);
                setPaySlipsData([]);
            } finally {
                setPaySlipsLoading(false);
            }
        };

        fetchEmployeePayslips();
    }, [selectedEmployee]);

    // Helper function to get month name
    const getMonthName = (monthNum) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[(monthNum - 1) % 12] || 'Unknown';
    };

    // Currency conversion rates (base: INR)
    const currencyRates = {
        'INR': { symbol: '₹', rate: 1, name: 'Indian Rupee' },
        'USD': { symbol: '$', rate: 0.012, name: 'US Dollar' },
        'AUD': { symbol: 'A$', rate: 0.018, name: 'Australian Dollar' },
        'CAD': { symbol: 'C$', rate: 0.016, name: 'Canadian Dollar' },
        'GBP': { symbol: '£', rate: 0.0096, name: 'British Pound' },
        'JPY': { symbol: '¥', rate: 1.79, name: 'Japanese Yen' },
        'MXN': { symbol: 'MX$', rate: 0.20, name: 'Mexican Peso' }
    };

    // Tab sections arranged in 2 rows
    const tabSections = [
        // Row 1
        ['PAY PACKAGE', 'SALARY REVISIONS', 'BONUS ALLOCATION', 'PAY SLIPS'],

        // ['PAY PACKAGE', 'CTC PRORATION', 'FLEXI COMPONENTS', 'IT DECLARATION', 'TAX SHEET', 'PAY SLIPS'],
        // Row 2
        // ['OFF CYCLE PAY SLIPS', 'EXTRA PAYMENTS', 'LOANS', 'ADVANCES / EXTRA DEDUCTIONS', 'HOLD SALARY / REPAYMENT', 'PERQUISITES']
    ];

    // Salary structure options
    const salaryStructureOptions = [
        'OT Salary Structure',
        'Executive Salary Structure',
        'Manager Salary Structure',
        'Senior Manager Salary Structure',
        'Director Salary Structure',
        'VP Salary Structure',
        'C-Level Salary Structure',
        'Consultant Salary Structure',
        'Intern Salary Structure',
        'Contract Salary Structure'
    ];

    // Salary Component Templates
    const salaryComponentTemplates = {
        earnings: [
            { name: 'Basic Salary', category: 'fixed', type: 'earning' },
            { name: 'HRA (House Rent Allowance)', category: 'allowance', type: 'earning' },
            { name: 'Special Allowance', category: 'allowance', type: 'earning' },
            { name: 'Variable Pay', category: 'variable', type: 'earning' },
            { name: 'Conveyance Allowance', category: 'allowance', type: 'earning' },
            { name: 'Medical Allowance', category: 'allowance', type: 'earning' },
            { name: 'Food Allowance', category: 'allowance', type: 'earning' },
            { name: 'LTA (Leave Travel Allowance)', category: 'allowance', type: 'earning' },
            { name: 'Performance Bonus', category: 'bonus', type: 'earning' },
            { name: 'Joining Bonus', category: 'bonus', type: 'earning' },
            { name: 'Retention Bonus', category: 'bonus', type: 'earning' },
            { name: 'Education Allowance', category: 'allowance', type: 'earning' },
            { name: 'Mobile/Communication Allowance', category: 'allowance', type: 'earning' },
            { name: 'Shift Allowance', category: 'allowance', type: 'earning' },
            { name: 'Overtime Pay', category: 'variable', type: 'earning' }
        ],
        deductions: [
            { name: 'Provident Fund (PF)', category: 'statutory', type: 'deduction' },
            { name: 'Professional Tax', category: 'statutory', type: 'deduction' },
            { name: 'Income Tax (TDS)', category: 'statutory', type: 'deduction' },
            { name: 'ESI (Employee State Insurance)', category: 'statutory', type: 'deduction' },
            { name: 'Loan Repayment', category: 'other', type: 'deduction' },
            { name: 'Advance Salary Recovery', category: 'other', type: 'deduction' }
        ]
    };

    // Employee data is now managed by EmployeeContext - automatically synchronized

    // Helper function to format date to DD/MM/YYYY
    const formatDateDDMMYYYY = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Helper function to generate financial years based on joining date
    const generateFinancialYears = (joiningDate) => {
        const joining = new Date(joiningDate);
        const currentDate = new Date();
        const years = [];

        // Determine financial year start month (April in India)
        const getFinancialYear = (date) => {
            const year = date.getFullYear();
            const month = date.getMonth(); // 0-11

            if (month >= 3) { // April (3) to March (next year)
                return `${year}-${(year + 1).toString().slice(-2)}`;
            } else {
                return `${year - 1}-${year.toString().slice(-2)}`;
            }
        };

        // Start from joining date's financial year
        let startYear = getFinancialYear(joining);
        const currentFY = getFinancialYear(currentDate);

        // Generate years from joining till current FY
        let year = parseInt(startYear.split('-')[0]);
        const currentYear = parseInt(currentFY.split('-')[0]);

        while (year <= currentYear) {
            years.push(`${year}-${(year + 1).toString().slice(-2)}`);
            year++;
        }

        return years.reverse(); // Most recent first
    };

    const handleEmployeeSelect = async (employeeId) => {
        const employee = employees.find(emp => emp.id === employeeId);
        setSelectedEmployee(employee);
        setPayPackageData(employee && employee.payPackage ? [...employee.payPackage] : []);

        // Generate financial years based on joining date
        if (employee && employee.joining_date) {
            const years = generateFinancialYears(employee.joining_date);
            setAvailableYears(years);
            if (years.length > 0) {
                setSelectedYear(years[0]); // Set to current FY
            }
        }

        // Load salary components from employee's pay_package or fetch fresh data
        if (employee && employee.id) {
            try {
                // Fetch latest employee data to get pay_package
                const response = await api.get(`/api/admin/employees/${employee.id}`);
                if (response.data && response.data.employee) {
                    const empData = response.data.employee;
                    const payPkg = empData.pay_package;

                    // Check if pay_package has salary_components
                    if (payPkg && payPkg.salary_components && Array.isArray(payPkg.salary_components)) {
                        // FIX: Filter out statutory deductions - they will be recalculated
                        setSalaryComponents(filterStatutoryDeductions(payPkg.salary_components));
                    } else if (empData.salary_components && Array.isArray(empData.salary_components)) {
                        // Fallback to direct column if it exists
                        // FIX: Filter out statutory deductions
                        setSalaryComponents(filterStatutoryDeductions(empData.salary_components));
                    } else {
                        // No pay package data - keep default empty or show add option
                        setSalaryComponents([]);
                    }
                }
            } catch (error) {
                console.error('Error loading employee data:', error);
                // Keep default components if fetch fails
            }
        }

        if (employee) {
            // Set different flexi values based on employee
            if (employee.id === 'EMP002') { // John Doe
                setFlexiValues({
                    mobileAllowance: 18000,
                    fuelAllowance: 12000
                });
                // Set John Doe's specific flexi component statuses
                setFlexiComponentStatuses({
                    mobileAllowance: { status: 'OPEN', fromDate: '2023-09-01', upToDate: '2024-08-31' },
                    fuelAllowance: { status: 'Pending', fromDate: '2023-09-20', upToDate: 'NA' }
                });
            } else { // Jennifer Julian and others
                setFlexiValues({
                    mobileAllowance: 25000,
                    fuelAllowance: 30000
                });
                // Set default flexi component statuses
                setFlexiComponentStatuses({
                    mobileAllowance: { status: 'OPEN', fromDate: '2023-04-01', upToDate: '2023-06-30' },
                    fuelAllowance: { status: 'OPEN', fromDate: '2023-04-01', upToDate: '2023-06-30' }
                });
            }
        }
    };

    const formatNumber = (num) => {
        return Math.round(num).toLocaleString('en-IN');
    };

    const convertCurrency = (amount, fromCurrency = 'INR', toCurrency = globalCurrency) => {
        if (fromCurrency === toCurrency) return amount;
        const inrAmount = amount / currencyRates[fromCurrency].rate;
        return inrAmount * currencyRates[toCurrency].rate;
    };

    const formatCurrencyValue = (value) => {
        return `${currencyRates[globalCurrency].symbol}${formatNumber(convertCurrency(value))}`;
    };

    const handleAddNewRow = () => {
        const today = new Date().toISOString().split('T')[0];
        const newRow = {
            date: today,
            monthlyFixed: 0,
            totalCTC: 0,
            baseCurrencyCTC: 0,
            variablePay: 0,
            salaryStructure: 'OT Salary Structure',
            annual: 'Annual'
        };

        // Add new row at the beginning (top)
        setPayPackageData([newRow, ...payPackageData]);
    };

    const validateDateOrder = (rowIndex, newDate) => {
        // Check if date is more recent than row below (older entries)
        if (rowIndex < payPackageData.length - 1) {
            const belowDate = new Date(payPackageData[rowIndex + 1].date);
            if (new Date(newDate) <= belowDate) {
                alert('Date must be more recent than the entry below');
                return false;
            }
        }

        // Check if date is older than row above (newer entries)
        if (rowIndex > 0) {
            const aboveDate = new Date(payPackageData[rowIndex - 1].date);
            if (new Date(newDate) >= aboveDate) {
                alert('Date must be older than the entry above');
                return false;
            }
        }

        return true;
    };

    const handleDateChange = (rowIndex, newDate) => {
        if (validateDateOrder(rowIndex, newDate)) {
            const updatedData = [...payPackageData];
            updatedData[rowIndex].date = newDate;
            setPayPackageData(updatedData);
            setEditingCell(null);
        }
    };

    const handleDeleteRow = (rowIndex) => {
        if (payPackageData.length > 1) {
            const updatedData = payPackageData.filter((_, idx) => idx !== rowIndex);
            setPayPackageData(updatedData);
        } else {
            alert('Cannot delete the last row');
        }
    };

    // Salary Component Handlers
    const handleComponentChange = (index, field, value) => {
        const updated = [...salaryComponents];
        updated[index][field] = value;

        // Auto-calculate monthly/annual based on view mode
        if (field === 'amount') {
            updated[index].monthly_amount = Math.round(parseFloat(value || 0) / 12);
        } else if (field === 'monthly_amount') {
            updated[index].amount = Math.round(parseFloat(value || 0) * 12);
        }

        setSalaryComponents(updated);
    };

    const handleAddComponent = (template) => {
        const newComponent = {
            name: template.name,
            amount: 0,
            monthly_amount: 0,
            type: template.type,
            is_custom: false,
            category: template.category
        };
        setSalaryComponents([...salaryComponents, newComponent]);
        setShowComponentSelector(false);
    };

    const handleAddCustomComponent = () => {
        if (!customComponentName.trim()) return;

        const newComponent = {
            name: customComponentName,
            amount: 0,
            monthly_amount: 0,
            type: componentType,
            is_custom: true,
            category: 'custom'
        };
        setSalaryComponents([...salaryComponents, newComponent]);
        setCustomComponentName('');
        setShowComponentSelector(false);
    };

    const handleRemoveComponent = (index) => {
        setSalaryComponents(salaryComponents.filter((_, i) => i !== index));
    };

    const calculateTotals = () => {
        // FIX: Use allSalaryComponents to include calculated statutory deductions (PT, PF, TDS)
        const earnings = allSalaryComponents
            .filter(c => c.type === 'earning')
            .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
        const deductions = allSalaryComponents
            .filter(c => c.type === 'deduction')
            .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
        return { earnings, deductions, net: earnings - deductions };
    };

    const handleSaveSalaryComponents = async () => {
        if (!selectedEmployee || !selectedEmployee.id) {
            alert('Please select an employee first');
            return;
        }

        try {
            const totalCTC = calculateTotals().earnings;

            // Update employee's pay_package with salary components
            const payPackageData = {
                annual_ctc: totalCTC,
                salary_components: salaryComponents,
                ctc: totalCTC, // Also store as ctc for compatibility
                currency: 'INR',
                effective_date: new Date().toISOString()
            };

            await api.put(`/api/admin/employees/${selectedEmployee.id}`, {
                pay_package: payPackageData
            });

            setIsEditingComponents(false);
            alert('Salary components saved successfully!');
        } catch (error) {
            console.error('Error saving compensation:', error);
            alert('Failed to save salary components. Please try again.');
        }
    };

    const handleAddYear = () => {
        if (newYear && !availableYears.includes(newYear)) {
            setAvailableYears([newYear, ...availableYears]);
            setSelectedYear(newYear);
            setNewYear('');
            setShowAddYearModal(false);
        }
    };

    const handleEditRow = (rowIndex) => {
        setEditingRow(editingRow === rowIndex ? null : rowIndex);
    };

    const handleUpdateField = (rowIndex, field, value) => {
        const updatedData = [...payPackageData];
        updatedData[rowIndex][field] = value;
        setPayPackageData(updatedData);
    };

    const handleAddVariablePay = (rowIndex) => {
        setEditingPayPackage({...payPackageData[rowIndex], index: rowIndex});
        setShowAddVariablePayModal(true);
    };

    const getCTCProrationData = (rowIndex) => {
        if (!payPackageData || !payPackageData[rowIndex]) return null;

        const currentRow = payPackageData[rowIndex];
        const startDate = new Date(currentRow.date);
        let endDate = new Date();

        // For first row (latest), show till current date
        // For other rows, show till next change date
        if (rowIndex > 0) {
            endDate = new Date(payPackageData[rowIndex - 1].date);
            endDate.setDate(endDate.getDate() - 1);
        }

        const months = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            months.push({
                month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                workingDays: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate(),
                ctcDays: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate(),
                absence: 0,
                amount: currentRow.monthlyFixed
            });
            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        return {
            startDate: startDate.toLocaleDateString('en-GB'),
            endDate: endDate.toLocaleDateString('en-GB'),
            months: months
        };
    };

    const handleShowProration = (rowIndex) => {
        const prorationData = getCTCProrationData(rowIndex);
        setSelectedProrationRow(prorationData);
        setShowCTCProrationModal(true);
    };

    const handleToggleFlexiEdit = () => {
        if (isFlexiEditable) {
            // Save logic here
            alert('Changes saved successfully!');
        }
        setIsFlexiEditable(!isFlexiEditable);
    };

    const handleFlexiValueChange = (field, value) => {
        const numValue = parseInt(value) || 0;

        // Validate ranges for default components
        if (field === 'mobileAllowance') {
            if (numValue >= 5000 && numValue <= 25000) {
                setFlexiValues({...flexiValues, [field]: numValue});
            }
        } else if (field === 'fuelAllowance') {
            if (numValue >= 0 && numValue <= 30000) {
                setFlexiValues({...flexiValues, [field]: numValue});
            }
        } else {
            // For custom components
            setFlexiValues({...flexiValues, [field]: numValue});
        }
    };

    const handleAddFlexiComponent = () => {
        // Validation
        if (!newFlexiComponent.name || !newFlexiComponent.name.trim()) {
            alert('Please enter a component name');
            return;
        }

        const lowerLimit = parseFloat(newFlexiComponent.lowerLimit);
        const upperLimit = parseFloat(newFlexiComponent.upperLimit);

        if (isNaN(lowerLimit) || lowerLimit < 0) {
            alert('Lower limit must be a valid number and cannot be negative');
            return;
        }

        if (isNaN(upperLimit) || upperLimit < 0) {
            alert('Upper limit must be a valid number and cannot be negative');
            return;
        }

        if (upperLimit <= lowerLimit) {
            alert('Upper limit must be higher than lower limit');
            return;
        }

        const componentKey = newFlexiComponent.name.replace(/\s+/g, '').toLowerCase();

        // Check if component with same name already exists
        if (customFlexiComponents.some(comp => comp.key === componentKey)) {
            alert('A component with this name already exists');
            return;
        }

        const today = new Date().toISOString().split('T')[0];
        const newComponent = {
            id: Date.now(),
            key: componentKey,
            name: newFlexiComponent.name.trim(),
            lowerLimit: lowerLimit,
            upperLimit: upperLimit,
            currency: globalCurrency, // Use global currency
            value: lowerLimit,
            status: 'OPEN',
            fromDate: today,
            upToDate: today
        };

        setCustomFlexiComponents([...customFlexiComponents, newComponent]);
        setFlexiValues({
            ...flexiValues,
            [componentKey]: lowerLimit
        });

        // Add to flexi history
        setFlexiHistory({
            ...flexiHistory,
            [componentKey]: [{
                status: 'OPEN',
                fromDate: today,
                toDate: today,
                changedOn: today,
                changedBy: 'Current User'
            }]
        });

        setNewFlexiComponent({
            name: '',
            lowerLimit: '',
            upperLimit: '',
            value: 0
        });
        setShowAddFlexiModal(false);
    };

    const handleDeleteFlexiComponent = (componentId, componentKey) => {
        if (window.confirm('Are you sure you want to delete this flexi component?')) {
            setCustomFlexiComponents(customFlexiComponents.filter(c => c.id !== componentId));
            const newFlexiValues = {...flexiValues};
            delete newFlexiValues[componentKey];
            setFlexiValues(newFlexiValues);
        }
    };

    const handleCustomFlexiValueChange = (componentKey, value, lowerLimit, upperLimit) => {
        const numValue = parseInt(value) || 0;
        if (numValue >= lowerLimit && numValue <= upperLimit) {
            setFlexiValues({...flexiValues, [componentKey]: numValue});
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'OPEN': return 'bg-green-100 text-green-800';
            case 'CLOSED': return 'bg-red-100 text-red-800';
            case 'Under Review': return 'bg-yellow-100 text-yellow-800';
            case 'Pending': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const toggleYearExpansion = (year) => {
        setExpandedYears(prev => ({
            ...prev,
            [year]: !prev[year]
        }));
    };

    // Pay slips handlers
    const handleSelectPaySlip = (paySlipId) => {
        setSelectedPaySlips(prev => {
            if (prev.includes(paySlipId)) {
                return prev.filter(id => id !== paySlipId);
            } else {
                return [...prev, paySlipId];
            }
        });
    };

    const handleSelectAllPaySlips = () => {
        if (selectedPaySlips.length === paySlipsData.length) {
            setSelectedPaySlips([]);
        } else {
            setSelectedPaySlips(paySlipsData.map(slip => slip.id));
        }
    };

    const handleToggleSelection = () => {
        setShowPaySlipCheckboxes(!showPaySlipCheckboxes);
        setSelectedPaySlips([]);
    };

    const handleDeleteSelectedPaySlips = () => {
        if (selectedPaySlips.length === 0) {
            alert('Please select payslips to delete');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedPaySlips.length} selected payslip(s)?`)) {
            setPaySlipsData(prev => prev.filter(slip => !selectedPaySlips.includes(slip.id)));
            setSelectedPaySlips([]);
            setShowPaySlipCheckboxes(false);
        }
    };

    const handleGenerateSalarySlip = () => {
        alert('Generate Salary Slip functionality would be implemented here');
    };

    const handleYearlyPayslips = () => {
        alert('Yearly Payslips functionality would be implemented here');
    };

    const handleForm16B = () => {
        alert('Form 16B functionality would be implemented here');
    };

    // Employee Selection Screen
    if (!selectedEmployee) {
        return (
            <div className="flex-1 p-6">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-8`}>
                        <h2 className={`text-2xl font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                            Select Employee for Compensation Management
                        </h2>
                        <div className="space-y-4">
                            <label className="block">
                                <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} font-medium mb-2 block`}>
                                    Choose Employee
                                </span>
                                <div className="relative">
                                    <select
                                        onChange={(e) => handleEmployeeSelect(e.target.value)}
                                        className={`w-full p-3 pr-10 border ${
                                            theme === 'dark'
                                                ? 'border-gray-600 bg-gray-700 text-white'
                                                : 'border-gray-300 bg-white text-gray-900'
                                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none`}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>-- Select an Employee --</option>
                                        {employees.map(employee => (
                                            <option key={employee.id} value={employee.id}>
                                                {employee.name} - {employee.designation} ({employee.id})
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown className={`absolute right-3 top-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'} pointer-events-none`} size={20} />
                                </div>
                            </label>
                            <div className={`mt-6 p-4 ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg`}>
                                <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                                    Select an employee to view and manage their compensation details including salary structure,
                                    flexi components, tax declarations, and other payroll information.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderPayPackage = () => {
        // Show loader while fetching pay group data
        if (loadingPayGroup) {
            return (
                <div className="p-6 flex flex-col items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 dark:border-blue-400"></div>
                    <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        Loading salary breakdown...
                    </p>
                </div>
            );
        }

        // Show "No pay group configured" message
        if (payGroupStatus === 'not_configured') {
            return (
                <div className="p-6 flex flex-col items-center justify-center h-64">
                    <div className="flex flex-col items-center justify-center p-12 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-2 border-dashed border-yellow-300 dark:border-yellow-700">
                        <Info className="w-16 h-16 text-yellow-600 dark:text-yellow-400 mb-4" />
                        <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            No Pay Group Configured
                        </h4>
                        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                            This employee has not been assigned to a pay group yet. Please assign them to a pay group in the Pay Groups tab to see their salary breakdown.
                        </p>
                    </div>
                </div>
            );
        }

        return (
        <div className="p-6">
            {/*<div className="flex justify-between items-center mb-4">*/}
            {/*    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Pay Package for {selectedYear}</h3>*/}
            {/*    <div className="flex gap-2">*/}
            {/*        <button*/}
            {/*            onClick={handleAddNewRow}*/}
            {/*            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"*/}
            {/*        >*/}
            {/*            <Plus size={16} />*/}
            {/*            Add*/}
            {/*        </button>*/}
            {/*        <button*/}
            {/*            onClick={() => handleAddVariablePay(0)}*/}
            {/*            className="px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50"*/}
            {/*        >*/}
            {/*            ADD VARIABLE PAY*/}
            {/*        </button>*/}
            {/*    </div>*/}
            {/*</div>*/}
            {/*<div className="overflow-x-auto">*/}
            {/*    <table className="w-full border-collapse">*/}
            {/*        <thead>*/}
            {/*        <tr className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>When Was The Change?</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Currency</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Monthly Fixed CTC</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Total CTC</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Base Currency CTC</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Variable Pay</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Salary Structure</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Annual/Monthly</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Actions</th>*/}
            {/*            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>CTC Proration</th>*/}
            {/*        </tr>*/}
            {/*        </thead>*/}
            {/*        <tbody>*/}
            {/*        {payPackageData && payPackageData.map((row, idx) => (*/}
            {/*            <tr key={idx} className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>*/}
            {/*                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>*/}
            {/*                    {editingCell === `date-${idx}` ? (*/}
            {/*                        <input*/}
            {/*                            type="date"*/}
            {/*                            value={row.date}*/}
            {/*                            onChange={(e) => handleDateChange(idx, e.target.value)}*/}
            {/*                            onBlur={() => setEditingCell(null)}*/}
            {/*                            className={`p-1 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}*/}
            {/*                            autoFocus*/}
            {/*                        />*/}
            {/*                    ) : (*/}
            {/*                        <span*/}
            {/*                            onClick={() => setEditingCell(`date-${idx}`)}*/}
            {/*                            className="cursor-pointer hover:text-blue-600"*/}
            {/*                        >*/}
            {/*          {formatDateDDMMYYYY(row.date)}*/}
            {/*        </span>*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{globalCurrency}</td>*/}
            {/*                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>*/}
            {/*                    {editingRow === idx ? (*/}
            {/*                        <input*/}
            {/*                            type="number"*/}
            {/*                            value={row.monthlyFixed}*/}
            {/*                            onChange={(e) => handleUpdateField(idx, 'monthlyFixed', e.target.value)}*/}
            {/*                            className={`w-full p-1 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}*/}
            {/*                        />*/}
            {/*                    ) : (*/}
            {/*                        formatCurrencyValue(row.monthlyFixed)*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>*/}
            {/*                    {editingRow === idx ? (*/}
            {/*                        <input*/}
            {/*                            type="number"*/}
            {/*                            value={row.totalCTC}*/}
            {/*                            onChange={(e) => handleUpdateField(idx, 'totalCTC', e.target.value)}*/}
            {/*                            className={`w-full p-1 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}*/}
            {/*                        />*/}
            {/*                    ) : (*/}
            {/*                        formatCurrencyValue(row.totalCTC)*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className="border p-3">*/}
            {/*                    {editingRow === idx ? (*/}
            {/*                        <input*/}
            {/*                            type="number"*/}
            {/*                            value={row.baseCurrencyCTC}*/}
            {/*                            onChange={(e) => handleUpdateField(idx, 'baseCurrencyCTC', e.target.value)}*/}
            {/*                            className="w-full p-1 border rounded"*/}
            {/*                        />*/}
            {/*                    ) : (*/}
            {/*                        formatCurrencyValue(row.baseCurrencyCTC)*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className="border p-3">*/}
            {/*                    {editingRow === idx ? (*/}
            {/*                        <input*/}
            {/*                            type="number"*/}
            {/*                            value={row.variablePay}*/}
            {/*                            onChange={(e) => handleUpdateField(idx, 'variablePay', e.target.value)}*/}
            {/*                            className="w-full p-1 border rounded"*/}
            {/*                        />*/}
            {/*                    ) : (*/}
            {/*                        formatCurrencyValue(row.variablePay)*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className="border p-3">*/}
            {/*                    {editingRow === idx ? (*/}
            {/*                        <select*/}
            {/*                            value={row.salaryStructure}*/}
            {/*                            onChange={(e) => handleUpdateField(idx, 'salaryStructure', e.target.value)}*/}
            {/*                            className="w-full p-1 border rounded"*/}
            {/*                        >*/}
            {/*                            {salaryStructureOptions.map(structure => (*/}
            {/*                                <option key={structure} value={structure}>{structure}</option>*/}
            {/*                            ))}*/}
            {/*                        </select>*/}
            {/*                    ) : (*/}
            {/*                        row.salaryStructure*/}
            {/*                    )}*/}
            {/*                </td>*/}
            {/*                <td className="border p-3">{row.annual}</td>*/}
            {/*                <td className="border p-3">*/}
            {/*                    <div className="flex gap-2">*/}
            {/*                        {editingRow === idx ? (*/}
            {/*                            <button*/}
            {/*                                onClick={() => setEditingRow(null)}*/}
            {/*                                className="text-green-600 hover:text-green-800"*/}
            {/*                            >*/}
            {/*                                <Save size={16} />*/}
            {/*                            </button>*/}
            {/*                        ) : (*/}
            {/*                            <button*/}
            {/*                                onClick={() => handleEditRow(idx)}*/}
            {/*                                className="text-gray-600 hover:text-gray-800"*/}
            {/*                            >*/}
            {/*                                <Edit3 size={16} />*/}
            {/*                            </button>*/}
            {/*                        )}*/}
            {/*                        <button*/}
            {/*                            onClick={() => handleDeleteRow(idx)}*/}
            {/*                            className="text-red-600 hover:text-red-800"*/}
            {/*                        >*/}
            {/*                            <Trash2 size={16} />*/}
            {/*                        </button>*/}
            {/*                    </div>*/}
            {/*                </td>*/}
            {/*                <td className="border p-3">*/}
            {/*                    <button*/}
            {/*                        onClick={() => handleShowProration(idx)}*/}
            {/*                        className="text-gray-600 hover:text-blue-600"*/}
            {/*                    >*/}
            {/*                        <MoreVertical size={16} />*/}
            {/*                    </button>*/}
            {/*                </td>*/}
            {/*            </tr>*/}
            {/*        ))}*/}
            {/*        </tbody>*/}
            {/*    </table>*/}
            {/*</div>*/}

            {/* Salary Component Breakdown Section */}
            <div className="mt-8 border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Salary Breakdown
                    </h3>
                    <div className="flex gap-2">
                        {/* FIX: Use allSalaryComponents to include calculated statutory */}
                        {allSalaryComponents.length > 0 && (
                            <>
                                {/* Monthly/Annual Toggle */}
                                <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
                                    <button
                                        onClick={() => setViewMode('annual')}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            viewMode === 'annual'
                                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                                : 'text-gray-600 dark:text-gray-400'
                                        }`}
                                    >
                                        Annual
                                    </button>
                                    <button
                                        onClick={() => setViewMode('monthly')}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            viewMode === 'monthly'
                                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                                : 'text-gray-600 dark:text-gray-400'
                                        }`}
                                    >
                                        Monthly
                                    </button>
                                </div>
                                {isEditingComponents ? (
                                    <>
                                        <button
                                            onClick={() => setShowComponentSelector(true)}
                                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                                        >
                                            <Plus size={16} />
                                            Add Component
                                        </button>
                                        <button
                                            onClick={handleSaveSalaryComponents}
                                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                                        >
                                            <Save size={16} />
                                            Save
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setIsEditingComponents(true)}
                                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-1"
                                    >
                                        <Edit3 size={16} />
                                        Edit
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Show Add Pay Package button if no components */}
                {/* FIX: Use allSalaryComponents to include calculated statutory */}
                {allSalaryComponents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                        <DollarSign className="w-16 h-16 text-gray-400 mb-4" />
                        <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            No Pay Package Configured
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Add a pay package for this employee to get started
                        </p>
                        <button
                            onClick={() => {
                                setIsEditingComponents(true);
                                setShowComponentSelector(true);
                            }}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-semibold"
                        >
                            <Plus size={20} />
                            Add Pay Package
                        </button>
                    </div>
                ) : (
                    <div>

                {/* Annual CTC Display */}
                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                        <DollarSign className="w-6 h-6 text-green-600" />
                        <div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Annual CTC</div>
                            <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                                {currencyRates[globalCurrency].symbol}{formatNumber(calculateTotals().earnings)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Earnings Section */}
                <div className="mb-6">
                    <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-3">EARNINGS</h4>
                    <div className="space-y-2">
                        {salaryComponents
                            .filter(comp => comp.type === 'earning')
                            .map((comp, index) => {
                                const actualIndex = salaryComponents.findIndex(c => c === comp);
                                return (
                                    <div
                                        key={actualIndex}
                                        className={`flex items-center gap-4 p-3 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}
                                    >
                                        <div className="flex-1">
                                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {comp.name}
                                            </span>
                                            {comp.is_custom && (
                                                <span className="ml-2 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-2 py-1 rounded">
                                                    Custom
                                                </span>
                                            )}
                                        </div>
                                        {isEditingComponents ? (
                                            <>
                                                <input
                                                    type="number"
                                                    value={viewMode === 'annual' ? comp.amount : comp.monthly_amount}
                                                    onChange={(e) =>
                                                        handleComponentChange(
                                                            actualIndex,
                                                            viewMode === 'annual' ? 'amount' : 'monthly_amount',
                                                            e.target.value
                                                        )
                                                    }
                                                    className={`w-32 px-3 py-1 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                                />
                                                <button
                                                    onClick={() => handleRemoveComponent(actualIndex)}
                                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </>
                                        ) : (
                                            <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {currencyRates[globalCurrency].symbol}{formatNumber((viewMode === 'annual' ? comp.amount : comp.monthly_amount) || 0)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>

                {/* Deductions Section - FIX: Use allSalaryComponents to include calculated statutory */}
                {allSalaryComponents.some(c => c.type === 'deduction') && (
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">DEDUCTIONS</h4>
                        <div className="space-y-2">
                            {allSalaryComponents
                                .filter(comp => comp.type === 'deduction')
                                .map((comp, index) => {
                                    // Check if this is a statutory component (auto-calculated, not editable)
                                    const isStatutory = comp.auto_calculated === true;
                                    // For non-statutory, find index in salaryComponents for editing
                                    const actualIndex = isStatutory ? -1 : salaryComponents.findIndex(c => c === comp);
                                    return (
                                        <div
                                            key={`deduction-${index}`}
                                            className={`flex items-center gap-4 p-3 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}
                                        >
                                            <div className="flex-1">
                                                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                    {comp.name}
                                                </span>
                                                {comp.is_custom && (
                                                    <span className="ml-2 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-2 py-1 rounded">
                                                        Custom
                                                    </span>
                                                )}
                                                {isStatutory && (
                                                    <span className="ml-2 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded">
                                                        Auto
                                                    </span>
                                                )}
                                            </div>
                                            {isEditingComponents && !isStatutory ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        value={viewMode === 'annual' ? comp.amount : comp.monthly_amount}
                                                        onChange={(e) =>
                                                            handleComponentChange(
                                                                actualIndex,
                                                                viewMode === 'annual' ? 'amount' : 'monthly_amount',
                                                                e.target.value
                                                            )
                                                        }
                                                        className={`w-32 px-3 py-1 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                                    />
                                                    <button
                                                        onClick={() => handleRemoveComponent(actualIndex)}
                                                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            ) : (
                                                <span className={`text-sm font-semibold text-red-600 dark:text-red-400`}>
                                                    -{currencyRates[globalCurrency].symbol}{formatNumber((viewMode === 'annual' ? comp.amount : comp.monthly_amount) || 0)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* Totals */}
                <div className={`border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} pt-4 space-y-2`}>
                    <div className="flex justify-between items-center text-sm">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Total Earnings:</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                            {currencyRates[globalCurrency].symbol}{formatNumber(viewMode === 'annual' ? calculateTotals().earnings : calculateTotals().earnings / 12)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Total Deductions:</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                            -{currencyRates[globalCurrency].symbol}{formatNumber(viewMode === 'annual' ? calculateTotals().deductions : calculateTotals().deductions / 12)}
                        </span>
                    </div>
                    <div className={`flex justify-between items-center text-lg font-bold border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} pt-2`}>
                        <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Net Pay:</span>
                        <span className="text-blue-600 dark:text-blue-400">
                            {currencyRates[globalCurrency].symbol}{formatNumber(viewMode === 'annual' ? calculateTotals().net : calculateTotals().net / 12)}
                        </span>
                    </div>
                </div>
                    </div>
                )}
            </div>
        </div>
    );
    };

    const renderCTCProration = () => {
        // Calculate complete CTC proration from joining to exit/current
        const calculateCompleteCTCProration = () => {
            if (!selectedEmployee || !payPackageData) return [];

            const startDate = new Date(selectedEmployee.joiningDate);
            const endDate = selectedEmployee.exitDate ? new Date(selectedEmployee.exitDate) : new Date();
            const prorationData = [];

            let currentDate = new Date(startDate);
            while (currentDate <= endDate) {
                const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                const year = currentDate.getFullYear();
                const workingDays = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

                // Find applicable salary for this month
                let applicableSalary = payPackageData[payPackageData.length - 1]; // Default to oldest
                for (let i = 0; i < payPackageData.length; i++) {
                    if (new Date(payPackageData[i].date) <= currentDate) {
                        applicableSalary = payPackageData[i];
                        break;
                    }
                }

                const basicSalary = applicableSalary.monthlyFixed * 0.4;
                const hra = applicableSalary.monthlyFixed * 0.2;
                const otherAllowances = applicableSalary.monthlyFixed * 0.4;

                // Calculate PF using proper logic with wage ceiling
                // Try to use PF from salary components if available (from pay group)
                // FIX: Use allSalaryComponents to include calculated statutory
                let pfAmount = 0;

                if (allSalaryComponents && allSalaryComponents.length > 0) {
                    // Try to find PF from allSalaryComponents first (from calculatedStatutoryComponents)
                    const pfComp = allSalaryComponents.find(c =>
                        c.category === 'statutory' &&
                        (c.name.toLowerCase().includes('provident') || c.name.toLowerCase().includes('pf'))
                    );

                    if (pfComp && pfComp.monthly_amount) {
                        pfAmount = pfComp.monthly_amount;
                    } else {
                        // Calculate using sync method with actual components
                        const basicComp = allSalaryComponents.find(c =>
                            c.name.toLowerCase().includes('basic')
                        );
                        const daComp = allSalaryComponents.find(c =>
                            c.name.toLowerCase().includes('da') ||
                            c.name.toLowerCase().includes('dearness')
                        );
                        const actualBasic = basicComp?.monthly_amount || (basicComp?.amount ? basicComp.amount / 12 : basicSalary);
                        const actualDA = daComp?.monthly_amount || (daComp?.amount ? daComp.amount / 12 : 0);
                        pfAmount = pfService.calculatePFSync(actualBasic, actualDA);
                    }
                } else {
                    // Fallback to percentage-based calculation with wage ceiling
                    pfAmount = pfService.calculatePFSync(basicSalary, 0);
                }

                const pf = pfAmount;

                // Calculate PT using sync fallback (proration is synchronous)
                // For accurate PT with exemptions/capping, use backend API in async context
                const monthlyGross = applicableSalary.monthlyFixed;
                const professionalTax = professionalTaxService.calculatePTSync(selectedEmployee?.state, monthlyGross);

                // Calculate TDS using sync fallback with annual gross
                // For accurate TDS with exemptions/regime selection, use backend API in async context
                const annualGross = applicableSalary.monthlyFixed * 12;
                const incomeTax = tdsService.calculateTDSSync(annualGross);
                const totalDeductions = pf + professionalTax + incomeTax;
                const netSalary = applicableSalary.monthlyFixed - totalDeductions;

                prorationData.push({
                    month: monthYear,
                    year: year,
                    workingDays: workingDays,
                    absence: 0,
                    basic: basicSalary,
                    hra: hra,
                    otherAllowances: otherAllowances,
                    gross: applicableSalary.monthlyFixed,
                    pf: pf,
                    professionalTax: professionalTax,
                    incomeTax: incomeTax,
                    totalDeductions: totalDeductions,
                    net: netSalary,
                    bonus: applicableSalary.variablePay / 12
                });

                currentDate.setMonth(currentDate.getMonth() + 1);
            }

            return prorationData;
        };

        // Group proration data by year
        const groupProrationByYear = (data) => {
            const grouped = {};
            data.forEach(item => {
                if (!grouped[item.year]) {
                    grouped[item.year] = {
                        months: [],
                        totalGross: 0,
                        totalNet: 0,
                        totalDeductions: 0,
                        totalBonus: 0
                    };
                }
                grouped[item.year].months.push(item);
                grouped[item.year].totalGross += item.gross;
                grouped[item.year].totalNet += item.net;
                grouped[item.year].totalDeductions += item.totalDeductions;
                grouped[item.year].totalBonus += item.bonus;
            });
            return grouped;
        };

        const prorationData = calculateCompleteCTCProration();
        const groupedData = groupProrationByYear(prorationData);
        const years = Object.keys(groupedData).sort((a, b) => b - a);

        const totalGross = prorationData.reduce((sum, item) => sum + item.gross, 0);
        const totalNet = prorationData.reduce((sum, item) => sum + item.net, 0);
        const totalDeductions = prorationData.reduce((sum, item) => sum + item.totalDeductions, 0);
        const totalBonus = prorationData.reduce((sum, item) => sum + item.bonus, 0);

        const handleDownloadPDF = () => {
            alert('PDF download functionality would be implemented here');
        };

        return (
            <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Complete CTC Proration</h3>
                    <button
                        onClick={handleDownloadPDF}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Download size={16} />
                        Download PDF
                    </button>
                </div>

                {selectedEmployee && (
                    <div className={`mb-6 grid grid-cols-4 gap-4 p-4 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Employee Name</p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedEmployee.name}</p>
                        </div>
                        <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Joining Date</p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{formatDateDDMMYYYY(selectedEmployee.joiningDate)}</p>
                        </div>
                        <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Exit Date</p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedEmployee.exitDate ? formatDateDDMMYYYY(selectedEmployee.exitDate) : 'Currently Active'}</p>
                        </div>
                        <div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Tenure</p>
                            <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{prorationData.length} months</p>
                        </div>
                    </div>
                )}

                <div className="mb-6 grid grid-cols-4 gap-4">
                    <div className={`p-4 rounded ${theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Gross Salary</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrencyValue(totalGross)}</p>
                    </div>
                    <div className={`p-4 rounded ${theme === 'dark' ? 'bg-green-900/30' : 'bg-green-50'}`}>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Net Salary</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrencyValue(totalNet)}</p>
                    </div>
                    <div className={`p-4 rounded ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'}`}>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Deductions</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrencyValue(totalDeductions)}</p>
                    </div>
                    <div className={`p-4 rounded ${theme === 'dark' ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Bonus</p>
                        <p className="text-xl font-bold text-yellow-600">{formatCurrencyValue(totalBonus)}</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                        <tr className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} rowSpan={2}>Year/Month</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} rowSpan={2}>Working Days</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} rowSpan={2}>Absence</th>
                            <th className={`border p-2 text-center ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} colSpan={4}>Earnings</th>
                            <th className={`border p-2 text-center ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} colSpan={4}>Deductions</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} rowSpan={2}>Net Salary</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`} rowSpan={2}>Bonus</th>
                        </tr>
                        <tr className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Basic</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>HRA</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Other</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Gross</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>PF</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>PT</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>IT</th>
                            <th className={`border p-2 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {years.map((year) => {
                            const yearData = groupedData[year];
                            const isExpanded = expandedYears[year];

                            return (
                                <React.Fragment key={year}>
                                    <tr className={`font-semibold ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>
                                            <button
                                                onClick={() => toggleYearExpansion(year)}
                                                className="flex items-center gap-2 text-left w-full"
                                            >
                                                {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                                {year} ({yearData.months.length} months)
                                            </button>
                                        </td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>-</td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>-</td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} colSpan={4}></td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(yearData.totalGross)}</td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} colSpan={3}></td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(yearData.totalDeductions)}</td>
                                        <td className={`border p-2 text-green-600 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>{formatCurrencyValue(yearData.totalNet)}</td>
                                        <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(yearData.totalBonus)}</td>
                                    </tr>
                                    {isExpanded && yearData.months.map((row, idx) => (
                                        <tr key={`${year}-${idx}`} className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                            <td className={`border p-2 pl-8 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{row.month}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{row.workingDays}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{row.absence}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.basic)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.hra)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.otherAllowances)}</td>
                                            <td className={`border p-2 font-semibold ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.gross)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.pf)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.professionalTax)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.incomeTax)}</td>
                                            <td className={`border p-2 font-semibold ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.totalDeductions)}</td>
                                            <td className={`border p-2 font-semibold text-green-600 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>{formatCurrencyValue(row.net)}</td>
                                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(row.bonus)}</td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                        <tfoot>
                        <tr className={`font-bold ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`} colSpan={6}>Grand Total</td>
                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(totalGross)}</td>
                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} colSpan={3}></td>
                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(totalDeductions)}</td>
                            <td className={`border p-2 text-green-600 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>{formatCurrencyValue(totalNet)}</td>
                            <td className={`border p-2 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(totalBonus)}</td>
                        </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        );
    };

    const renderFlexiComponents = () => {
        const getFlexiStatusDisplay = () => {
            const status = flexiStatus.status;
            const fromDate = formatDateDDMMYYYY(flexiStatus.fromDate);
            const toDate = (status === 'Pending' || status === 'Under Review')
                ? <span className="text-gray-500">NA</span>
                : formatDateDDMMYYYY(flexiStatus.upToDate);

            return (
                <span>
          {status} from {fromDate} to {toDate}
        </span>
            );
        };

        return (
            <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Flexi Components</h3>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowAddFlexiModal(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                        >
                            Add Customized Flexi Component
                        </button>
                        <button
                            onClick={() => setIsEditingFlexiComponents(!isEditingFlexiComponents)}
                            className={`px-4 py-2 rounded ${
                                isEditingFlexiComponents
                                    ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                                    : 'bg-gray-600 text-white hover:bg-gray-700'
                            }`}
                        >
                            {isEditingFlexiComponents ? 'Done Editing' : 'Edit Flexi Components'}
                        </button>
                        <button
                            onClick={handleToggleFlexiEdit}
                            className={`px-4 py-2 rounded ${
                                isFlexiEditable
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                            {isFlexiEditable ? 'SAVE' : 'EDIT'}
                        </button>
                    </div>
                </div>
                <div className="space-y-4">
                    {/* Default Flexi Components */}
                    <div className={`p-4 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                        <div className="flex items-center">
                            {isEditingFlexiComponents && (
                                <button
                                    className="mr-3 text-red-600 hover:text-red-800"
                                    onClick={() => alert('Default components cannot be deleted')}
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Mobile And Internet Allowance (5000-25000)</h4>
                                    <input
                                        type="number"
                                        value={flexiValues.mobileAllowance}
                                        onChange={(e) => handleFlexiValueChange('mobileAllowance', e.target.value)}
                                        disabled={!isFlexiEditable}
                                        min="5000"
                                        max="25000"
                                        className={`w-32 p-2 border rounded ${
                                            !isFlexiEditable
                                                ? theme === 'dark' ? 'bg-gray-600 border-gray-500 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'
                                                : theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                </div>
                                <div className={`flex gap-8 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <span>Monthly: {formatCurrencyValue(flexiValues.mobileAllowance)}</span>
                                    <span>Annual: {formatCurrencyValue(flexiValues.mobileAllowance * 12)}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <span
                                        onClick={() => {
                                            setSelectedFlexiComponent('mobileAllowance');
                                            setShowFlexiStatusModal(true);
                                        }}
                                        className={`inline-block px-3 py-1 rounded cursor-pointer text-xs ${getStatusColor(flexiComponentStatuses.mobileAllowance.status)}`}
                                    >
                                        {flexiComponentStatuses.mobileAllowance.status} from {formatDateDDMMYYYY(flexiComponentStatuses.mobileAllowance.fromDate)} to {formatDateDDMMYYYY(flexiComponentStatuses.mobileAllowance.upToDate)}
                                    </span>
                                    <button
                                        onClick={() => {
                                            setSelectedFlexiComponent('mobileAllowance');
                                            setShowFlexiHistoryModal(true);
                                        }}
                                        className="p-1 bg-gray-100 rounded-full hover:bg-gray-200"
                                        title="View history"
                                    >
                                        <Info size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`p-4 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                        <div className="flex items-center">
                            {isEditingFlexiComponents && (
                                <button
                                    className="mr-3 text-red-600 hover:text-red-800"
                                    onClick={() => alert('Default components cannot be deleted')}
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Fuel Allowance (0-30000)</h4>
                                    <input
                                        type="number"
                                        value={flexiValues.fuelAllowance}
                                        onChange={(e) => handleFlexiValueChange('fuelAllowance', e.target.value)}
                                        disabled={!isFlexiEditable}
                                        min="0"
                                        max="30000"
                                        className={`w-32 p-2 border rounded ${
                                            !isFlexiEditable
                                                ? theme === 'dark' ? 'bg-gray-600 border-gray-500 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'
                                                : theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                </div>
                                <div className={`flex gap-8 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <span>Monthly: {formatCurrencyValue(flexiValues.fuelAllowance)}</span>
                                    <span>Annual: {formatCurrencyValue(flexiValues.fuelAllowance * 12)}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <span
                                        onClick={() => {
                                            setSelectedFlexiComponent('fuelAllowance');
                                            setShowFlexiStatusModal(true);
                                        }}
                                        className={`inline-block px-3 py-1 rounded cursor-pointer text-xs ${getStatusColor(flexiComponentStatuses.fuelAllowance.status)}`}
                                    >
                                        {flexiComponentStatuses.fuelAllowance.status} from {formatDateDDMMYYYY(flexiComponentStatuses.fuelAllowance.fromDate)} to {formatDateDDMMYYYY(flexiComponentStatuses.fuelAllowance.upToDate)}
                                    </span>
                                    <button
                                        onClick={() => {
                                            setSelectedFlexiComponent('fuelAllowance');
                                            setShowFlexiHistoryModal(true);
                                        }}
                                        className="p-1 bg-gray-100 rounded-full hover:bg-gray-200"
                                        title="View history"
                                    >
                                        <Info size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Custom Flexi Components */}
                    {customFlexiComponents.map((component) => (
                        <div key={component.id} className={`p-4 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                            <div className="flex items-center">
                                {isEditingFlexiComponents && (
                                    <button
                                        className="mr-3 text-red-600 hover:text-red-800"
                                        onClick={() => handleDeleteFlexiComponent(component.id, component.key)}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                )}
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {component.name} ({component.lowerLimit}-{component.upperLimit})
                                        </h4>
                                        <input
                                            type="number"
                                            value={flexiValues[component.key] || component.lowerLimit}
                                            onChange={(e) => handleCustomFlexiValueChange(component.key, e.target.value, component.lowerLimit, component.upperLimit)}
                                            disabled={!isFlexiEditable}
                                            min={component.lowerLimit}
                                            max={component.upperLimit}
                                            className={`w-32 p-2 border rounded ${
                                                !isFlexiEditable
                                                    ? theme === 'dark' ? 'bg-gray-600 border-gray-500 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500'
                                                    : theme === 'dark' ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                        />
                                    </div>
                                    <div className={`flex gap-8 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        <span>Monthly: {currencyRates[globalCurrency].symbol}{formatNumber(flexiValues[component.key] || component.lowerLimit)}</span>
                                        <span>Annual: {currencyRates[globalCurrency].symbol}{formatNumber((flexiValues[component.key] || component.lowerLimit) * 12)}</span>
                                    </div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <span
                                            onClick={() => {
                                                setSelectedFlexiComponent(component.key);
                                                setShowFlexiStatusModal(true);
                                            }}
                                            className={`inline-block px-3 py-1 rounded cursor-pointer text-xs ${getStatusColor(component.status || 'OPEN')}`}
                                        >
                                            {component.status || 'OPEN'} from {formatDateDDMMYYYY(component.fromDate || new Date().toISOString().split('T')[0])} to {formatDateDDMMYYYY(component.upToDate || new Date().toISOString().split('T')[0])}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setSelectedFlexiComponent(component.key);
                                                setShowFlexiHistoryModal(true);
                                            }}
                                            className="p-1 bg-gray-100 rounded-full hover:bg-gray-200"
                                            title="View history"
                                        >
                                            <Info size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pending Flexi Component Requests Section */}
                <div className={`mt-8 p-4 border rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}>
                    <h4 className={`font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {selectedEmployee
                            ? `${selectedEmployee.name}'s Flexi Component Requests`
                            : 'All Pending Flexi Component Requests'
                        }
                    </h4>
                    {renderPendingFlexiRequests()}
                </div>
            </div>
        );
    };

    // Handle request actions (Manager/Admin)
    const handleRequestAction = (request, action) => {
        setSelectedRequest(request);
        setRequestAction(action);
        if (action === 'dismiss') {
            setShowRequestModal(true);
        } else {
            // For approve/under-review actions
            let newStatus = '';
            switch(action) {
                case 'approve':
                    newStatus = 'Open';
                    break;
                case 'under-review':
                    newStatus = 'Under Review';
                    break;
                default:
                    return;
            }
            updateRequestStatus(request.id, { status: newStatus }, 'Admin User');
        }
    };

    const handleDismissRequest = () => {
        if (!dismissReason.trim()) {
            alert('Please provide a reason for dismissal');
            return;
        }

        updateRequestStatus(selectedRequest.id, {
            status: 'Dismiss',
            dismissReason: dismissReason.trim(),
            feedback: `Request dismissed: ${dismissReason.trim()}`
        }, 'Admin User');

        setShowRequestModal(false);
        setSelectedRequest(null);
        setDismissReason('');
        setRequestAction('');
    };

    const renderPendingFlexiRequests = () => {
        // If an employee is selected, show only their requests, otherwise show all pending requests
        const pendingRequests = selectedEmployee
            ? getRequestsByEmployee(selectedEmployee.id).filter(req => req.status === 'Pending' || req.status === 'Under Review')
            : getPendingRequests();

        if (pendingRequests.length === 0) {
            return (
                <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Info size={48} className="mx-auto mb-4 opacity-50" />
                    <p>
                        {selectedEmployee
                            ? `No pending flexi component requests for ${selectedEmployee.name}.`
                            : 'No pending flexi component requests from any employee.'
                        }
                    </p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {pendingRequests.map((request) => (
                    <div key={request.id} className={`border rounded-lg p-4 ${
                        theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-gray-50'
                    }`}>
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <h5 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {request.title}
                                </h5>
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Employee: {request.employeeName} ({request.employeeId})
                                </p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Amount: {request.currency} {request.amount.toLocaleString()}
                                </p>
                            </div>
                            <span className={`px-3 py-1 text-xs rounded ${
                                request.status === 'Pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-blue-100 text-blue-800'
                            }`}>
                                {request.status}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                                <span className={`block text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Request Date: {request.requestDate}
                                </span>
                                <span className={`block text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Reporting Manager: {request.reportingManager}
                                </span>
                            </div>
                            <div>
                                {request.supportingDocument && (
                                    <span className={`block text-xs ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                                        Supporting Document: {request.supportingDocument.name}
                                    </span>
                                )}
                                {request.feedback && (
                                    <span className={`block text-xs ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                        Feedback: {request.feedback}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-2">
                            {request.status === 'Pending' && (
                                <>
                                    <button
                                        onClick={() => handleRequestAction(request, 'under-review')}
                                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                                    >
                                        Move to Under Review
                                    </button>
                                    <button
                                        onClick={() => handleRequestAction(request, 'approve')}
                                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                    >
                                        Approve
                                    </button>
                                </>
                            )}
                            {request.status === 'Under Review' && (
                                <button
                                    onClick={() => handleRequestAction(request, 'approve')}
                                    className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                >
                                    Approve
                                </button>
                            )}
                            <button
                                onClick={() => handleRequestAction(request, 'dismiss')}
                                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleCreateRevision = async () => {
        if (!selectedEmployee) {
            alert('Please select an employee first');
            return;
        }

        // Validate required fields
        if (!revisionFormData.effective_from) {
            alert('Please select an effective from date');
            return;
        }

        if (!revisionFormData.new_annual_ctc || revisionFormData.new_annual_ctc <= 0) {
            alert('Please enter a valid new annual CTC');
            return;
        }

        try {
            const newCtc = parseFloat(revisionFormData.new_annual_ctc);
            const oldCtc = salaryComponents.length > 0 ? calculateTotals().earnings : 0;

            // Calculate new salary components proportionally based on CTC ratio
            // This maintains the same percentage distribution across components
            let newSalaryComponents = salaryComponents;
            if (oldCtc > 0 && newCtc !== oldCtc) {
                const ratio = newCtc / oldCtc;
                newSalaryComponents = salaryComponents.map(comp => ({
                    ...comp,
                    amount: Math.round(comp.amount * ratio),
                    monthly_amount: Math.round((comp.amount * ratio) / 12)
                }));
                console.log('📈 Calculated new salary components with ratio:', ratio, newSalaryComponents);
            }

            const revisionData = {
                ...revisionFormData,
                new_annual_ctc: newCtc,
                old_annual_ctc: oldCtc || null,
                new_salary_components: newSalaryComponents
            };

            console.log('📈 Creating revision with data:', revisionData);
            await api.post(`/api/salary-revisions/employees/${selectedEmployee.id}/revisions`, revisionData);

            // Refresh revisions list
            const response = await api.get(`/api/salary-revisions/employees/${selectedEmployee.id}/revisions`);
            if (response.data && response.data.revisions) {
                setSalaryRevisions(response.data.revisions);
            }

            // Also refresh the employee's pay package data to reflect the update
            try {
                const empResponse = await api.get(`/api/admin/employees/${selectedEmployee.id}`);
                if (empResponse.data && empResponse.data.employee) {
                    const empData = empResponse.data.employee;
                    const payPkg = empData.pay_package;

                    if (payPkg && payPkg.salary_components && Array.isArray(payPkg.salary_components)) {
                        // FIX: Filter out statutory deductions - they will be recalculated
                        setSalaryComponents(filterStatutoryDeductions(payPkg.salary_components));
                    }
                }
            } catch (error) {
                console.error('Error refreshing pay package:', error);
            }

            // Reset form and close modal
            setShowRevisionModal(false);
            setRevisionFormData({
                revision_date: new Date().toISOString().split('T')[0],
                effective_from: '',
                revision_type: 'annual_increment',
                new_annual_ctc: '',
                reason: '',
                remarks: ''
            });

            alert('Salary revision created successfully!');
        } catch (error) {
            console.error('Error creating revision:', error);
            alert('Failed to create salary revision. Please try again.');
        }
    };

    // Handle edit revision - open modal with revision data
    const handleEditRevision = (revision) => {
        // Don't allow editing if already confirmed in payroll
        if (revision.payroll_confirmation_status === 'confirmed') {
            alert('Cannot edit a revision that has already been applied in payroll.');
            return;
        }

        setEditingRevision(revision);
        setRevisionFormData({
            revision_date: revision.revision_date ? revision.revision_date.split('T')[0] : new Date().toISOString().split('T')[0],
            effective_from: revision.effective_from ? revision.effective_from.split('T')[0] : '',
            revision_type: revision.revision_type || 'annual_increment',
            new_annual_ctc: revision.new_annual_ctc || '',
            reason: revision.reason || '',
            remarks: revision.remarks || ''
        });
        setShowRevisionModal(true);
    };

    // Handle update revision
    const handleUpdateRevision = async () => {
        if (!editingRevision) return;

        setRevisionActionLoading(true);
        try {
            const updateData = {
                revision_date: revisionFormData.revision_date,
                effective_from: revisionFormData.effective_from,
                revision_type: revisionFormData.revision_type,
                new_annual_ctc: parseFloat(revisionFormData.new_annual_ctc),
                reason: revisionFormData.reason,
                remarks: revisionFormData.remarks
            };

            await api.put(`/api/salary-revisions/revisions/${editingRevision.id}`, updateData);

            // Refresh revisions list
            setRevisionRefreshKey(prev => prev + 1);

            // Reset form and close modal
            setShowRevisionModal(false);
            setEditingRevision(null);
            setRevisionFormData({
                revision_date: new Date().toISOString().split('T')[0],
                effective_from: '',
                revision_type: 'annual_increment',
                new_annual_ctc: '',
                reason: '',
                remarks: ''
            });

            alert('Salary revision updated successfully!');
        } catch (error) {
            console.error('Error updating revision:', error);
            alert('Failed to update salary revision. Please try again.');
        } finally {
            setRevisionActionLoading(false);
        }
    };

    // Handle delete revision
    const handleDeleteRevision = async (revisionId) => {
        setRevisionActionLoading(true);
        try {
            await api.delete(`/api/salary-revisions/revisions/${revisionId}`);

            // Refresh revisions list
            setRevisionRefreshKey(prev => prev + 1);
            setShowDeleteConfirm(null);

            alert('Salary revision deleted successfully!');
        } catch (error) {
            console.error('Error deleting revision:', error);
            alert('Failed to delete salary revision. Please try again.');
        } finally {
            setRevisionActionLoading(false);
        }
    };

    const renderSalaryRevisions = () => (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Salary Revision History
                </h3>
                <div className="flex gap-2">
                    <button
                        onClick={() => setRevisionRefreshKey(prev => prev + 1)}
                        className={`px-3 py-2 rounded flex items-center gap-2 ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                        title="Refresh revisions"
                    >
                        <RefreshCw size={16} />
                    </button>
                    <button
                        onClick={() => setShowRevisionModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                    >
                        <Plus size={16} />
                        New Salary Revision
                    </button>
                </div>
            </div>

            {salaryRevisions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                    <Info className="w-16 h-16 text-gray-400 mb-4" />
                    <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        No Salary Revisions Yet
                    </h4>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Create a new salary revision to track changes
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {salaryRevisions.map((revision) => (
                        <div
                            key={revision.id}
                            className={`p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <h4 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {revision.revision_type === 'annual_increment' && 'Annual Increment'}
                                            {revision.revision_type === 'promotion' && 'Promotion'}
                                            {revision.revision_type === 'special_revision' && 'Special Revision'}
                                            {revision.revision_type === 'market_adjustment' && 'Market Adjustment'}
                                        </h4>
                                        {revision.increment_percentage && (
                                            <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-sm font-semibold rounded-full">
                                                +{revision.increment_percentage}%
                                            </span>
                                        )}
                                        {/* Payroll Confirmation Status Badge with Tooltip */}
                                        {revision.payroll_confirmation_status === 'confirmed' ? (
                                            <span
                                                className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs font-medium rounded-full flex items-center gap-1 cursor-help"
                                                title="This revision has been applied in payroll and cannot be modified"
                                            >
                                                <CheckCircle size={12} /> Applied
                                            </span>
                                        ) : revision.payroll_confirmation_status === 'deferred' ? (
                                            <span
                                                className="px-3 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-xs font-medium rounded-full cursor-help"
                                                title="This revision was postponed and will appear in the next payroll period"
                                            >
                                                Deferred
                                            </span>
                                        ) : (
                                            <span
                                                className="px-3 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs font-medium rounded-full cursor-help"
                                                title="This revision needs to be confirmed or deferred during payroll processing"
                                            >
                                                Pending Confirmation
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                                        <span>Effective from: {new Date(revision.effective_from).toLocaleDateString()}</span>
                                        <span>•</span>
                                        <span>Revised on: {new Date(revision.revision_date).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">New CTC</div>
                                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                        ₹{formatNumber(revision.new_annual_ctc)}
                                    </div>
                                    {revision.old_annual_ctc && (
                                        <div className="text-sm text-gray-500 dark:text-gray-500">
                                            Previous: ₹{formatNumber(revision.old_annual_ctc)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {revision.reason && (
                                <div className="mb-3">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Reason: </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{revision.reason}</span>
                                </div>
                            )}

                            {revision.remarks && (
                                <div className="mb-3">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Remarks: </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{revision.remarks}</span>
                                </div>
                            )}

                            {revision.approved_by_name && (
                                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                    <span className="text-xs text-gray-500 dark:text-gray-500">
                                        Approved by {revision.approved_by_name} on {new Date(revision.approval_date).toLocaleDateString()}
                                    </span>
                                </div>
                            )}

                            {/* Edit/Delete Actions - only show if not confirmed */}
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                                {revision.payroll_confirmation_status !== 'confirmed' ? (
                                    <>
                                        <button
                                            onClick={() => handleEditRevision(revision)}
                                            className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-800 flex items-center gap-1"
                                        >
                                            <Edit3 size={14} /> Edit
                                        </button>
                                        <button
                                            onClick={() => setShowDeleteConfirm(revision.id)}
                                            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800 flex items-center gap-1"
                                        >
                                            <Trash2 size={14} /> Delete
                                        </button>
                                    </>
                                ) : (
                                    <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                        Applied in payroll - cannot modify
                                    </span>
                                )}
                            </div>

                            {/* Delete Confirmation Inline - Enhanced with consequences */}
                            {showDeleteConfirm === revision.id && (
                                <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="p-2 bg-red-100 dark:bg-red-800 rounded-full">
                                            <Trash2 size={16} className="text-red-600 dark:text-red-200" />
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                                                Delete Salary Revision?
                                            </h4>
                                            <p className="text-sm text-red-700 dark:text-red-300">
                                                This will permanently remove the {revision.revision_type.replace(/_/g, ' ')} revision with CTC ₹{revision.new_annual_ctc?.toLocaleString('en-IN')}.
                                            </p>
                                            <ul className="mt-2 text-xs text-red-600 dark:text-red-400 list-disc list-inside space-y-1">
                                                <li>This action cannot be undone</li>
                                                <li>Historical records will be lost</li>
                                                <li>Salary calculations will revert to previous revision</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <button
                                            onClick={() => setShowDeleteConfirm(null)}
                                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 font-medium"
                                            disabled={revisionActionLoading}
                                        >
                                            Keep Revision
                                        </button>
                                        <button
                                            onClick={() => handleDeleteRevision(revision.id)}
                                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium"
                                            disabled={revisionActionLoading}
                                        >
                                            {revisionActionLoading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                                            Yes, Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* New/Edit Revision Modal */}
            {showRevisionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {editingRevision ? 'Edit Salary Revision' : 'Create Salary Revision'}
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowRevisionModal(false);
                                        setEditingRevision(null);
                                        setRevisionFormData({
                                            revision_date: new Date().toISOString().split('T')[0],
                                            effective_from: '',
                                            revision_type: 'annual_increment',
                                            new_annual_ctc: '',
                                            reason: '',
                                            remarks: ''
                                        });
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Current CTC Info Banner */}
                            {!editingRevision && (
                                <div className={`mb-4 p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <span className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>Current Annual CTC:</span>
                                            <span className={`ml-2 text-lg font-bold ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'}`}>
                                                ₹{(calculateTotals().earnings || 0).toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                        {revisionFormData.new_annual_ctc && parseFloat(revisionFormData.new_annual_ctc) > 0 && (
                                            <div className={`px-3 py-1 rounded-full ${
                                                parseFloat(revisionFormData.new_annual_ctc) > calculateTotals().earnings
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            }`}>
                                                <span className="text-sm font-semibold">
                                                    {(() => {
                                                        const currentCTC = calculateTotals().earnings || 0;
                                                        const newCTC = parseFloat(revisionFormData.new_annual_ctc) || 0;
                                                        if (currentCTC === 0) return 'New';
                                                        const pctChange = ((newCTC - currentCTC) / currentCTC * 100).toFixed(1);
                                                        return pctChange >= 0 ? `+${pctChange}%` : `${pctChange}%`;
                                                    })()}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Revision Date <span className="text-red-500">*</span>
                                        </label>
                                        <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                            When this revision was approved/created
                                        </p>
                                        <input
                                            type="date"
                                            value={revisionFormData.revision_date}
                                            onChange={(e) => setRevisionFormData({...revisionFormData, revision_date: e.target.value})}
                                            className={`w-full px-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                            Effective From <span className="text-red-500">*</span>
                                        </label>
                                        <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                            When the new salary takes effect in payroll
                                        </p>
                                        <input
                                            type="date"
                                            value={revisionFormData.effective_from}
                                            onChange={(e) => setRevisionFormData({...revisionFormData, effective_from: e.target.value})}
                                            className={`w-full px-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Revision Type
                                    </label>
                                    <select
                                        value={revisionFormData.revision_type}
                                        onChange={(e) => setRevisionFormData({...revisionFormData, revision_type: e.target.value})}
                                        className={`w-full px-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    >
                                        <option value="annual_increment">Annual Increment</option>
                                        <option value="promotion">Promotion</option>
                                        <option value="special_revision">Special Revision</option>
                                        <option value="market_adjustment">Market Adjustment</option>
                                    </select>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        New Annual CTC <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>₹</span>
                                        <input
                                            type="number"
                                            value={revisionFormData.new_annual_ctc}
                                            onChange={(e) => setRevisionFormData({...revisionFormData, new_annual_ctc: e.target.value})}
                                            placeholder="Enter new annual CTC"
                                            className={`w-full pl-8 pr-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                        />
                                    </div>
                                    {revisionFormData.new_annual_ctc && (
                                        <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                            Monthly: ₹{(parseFloat(revisionFormData.new_annual_ctc) / 12).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Reason
                                    </label>
                                    <input
                                        type="text"
                                        value={revisionFormData.reason}
                                        onChange={(e) => setRevisionFormData({...revisionFormData, reason: e.target.value})}
                                        placeholder="Enter reason for revision"
                                        className={`w-full px-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    />
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                        Remarks
                                    </label>
                                    <textarea
                                        value={revisionFormData.remarks}
                                        onChange={(e) => setRevisionFormData({...revisionFormData, remarks: e.target.value})}
                                        placeholder="Additional remarks (optional)"
                                        rows={3}
                                        className={`w-full px-3 py-2 border rounded-lg ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowRevisionModal(false);
                                        setEditingRevision(null);
                                        setRevisionFormData({
                                            revision_date: new Date().toISOString().split('T')[0],
                                            effective_from: '',
                                            revision_type: 'annual_increment',
                                            new_annual_ctc: '',
                                            reason: '',
                                            remarks: ''
                                        });
                                    }}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    disabled={revisionActionLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={editingRevision ? handleUpdateRevision : handleCreateRevision}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                    disabled={revisionActionLoading}
                                >
                                    {revisionActionLoading && <Loader2 size={16} className="animate-spin" />}
                                    {editingRevision ? 'Update Revision' : 'Create Revision'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    // ─── Bonus Allocation Handler Functions ──────────────────────────────
    const bonusTypes = ['Performance Bonus', 'Festival Bonus', 'Referral Bonus', 'Joining Bonus', 'Retention Bonus', 'Ad-hoc Bonus'];
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

    const isBonusPeriodProcessed = (bonus) => {
        return processedPayrollPeriods.has(`${bonus.month}-${bonus.year}`) || bonus.status === 'processed';
    };

    const handleCreateBonus = async () => {
        if (!bonusFormData.bonusType || !bonusFormData.amount || !bonusFormData.month || !bonusFormData.year) return;
        setBonusActionLoading(true);
        try {
            await api.post('/api/compensation/bonus-allocations', {
                employeeId: selectedEmployee.id,
                bonusType: bonusFormData.bonusType,
                amount: parseFloat(bonusFormData.amount),
                month: parseInt(bonusFormData.month),
                year: parseInt(bonusFormData.year),
                reason: bonusFormData.reason
            });
            setShowBonusModal(false);
            setEditingBonus(null);
            setBonusFormData({ bonusType: 'Performance Bonus', amount: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), reason: '' });
            // Re-fetch bonus allocations
            const response = await api.get(`/api/compensation/employees/${selectedEmployee.id}/bonus-allocations`);
            if (response.data?.bonuses) setBonusAllocations(response.data.bonuses);
        } catch (error) {
            console.error('Error creating bonus allocation:', error);
            alert('Failed to create bonus allocation: ' + (error.response?.data?.error || error.message));
        } finally {
            setBonusActionLoading(false);
        }
    };

    const handleUpdateBonus = async () => {
        if (!editingBonus || !bonusFormData.amount) return;
        setBonusActionLoading(true);
        try {
            await api.put(`/api/compensation/bonus-allocations/${editingBonus.id}`, {
                bonusType: bonusFormData.bonusType,
                amount: parseFloat(bonusFormData.amount),
                month: parseInt(bonusFormData.month),
                year: parseInt(bonusFormData.year),
                reason: bonusFormData.reason
            });
            setShowBonusModal(false);
            setEditingBonus(null);
            setBonusFormData({ bonusType: 'Performance Bonus', amount: '', month: new Date().getMonth() + 1, year: new Date().getFullYear(), reason: '' });
            const response = await api.get(`/api/compensation/employees/${selectedEmployee.id}/bonus-allocations`);
            if (response.data?.bonuses) setBonusAllocations(response.data.bonuses);
        } catch (error) {
            console.error('Error updating bonus allocation:', error);
            alert('Failed to update bonus allocation: ' + (error.response?.data?.error || error.message));
        } finally {
            setBonusActionLoading(false);
        }
    };

    const handleDeleteBonus = async (bonusId) => {
        if (!window.confirm('Are you sure you want to cancel this bonus allocation?')) return;
        try {
            await api.delete(`/api/compensation/bonus-allocations/${bonusId}`);
            const response = await api.get(`/api/compensation/employees/${selectedEmployee.id}/bonus-allocations`);
            if (response.data?.bonuses) setBonusAllocations(response.data.bonuses);
        } catch (error) {
            console.error('Error deleting bonus allocation:', error);
            alert('Failed to cancel bonus allocation: ' + (error.response?.data?.error || error.message));
        }
    };

    const openEditBonus = (bonus) => {
        setEditingBonus(bonus);
        setBonusFormData({
            bonusType: bonus.bonus_type,
            amount: bonus.amount,
            month: bonus.month,
            year: bonus.year,
            reason: bonus.reason || ''
        });
        setShowBonusModal(true);
    };

    const openNewBonus = () => {
        setEditingBonus(null);
        setBonusFormData({
            bonusType: 'Performance Bonus',
            amount: '',
            month: new Date().getMonth() + 1,
            year: new Date().getFullYear(),
            reason: ''
        });
        setShowBonusModal(true);
    };

    // ─── Bonus Allocation Render ─────────────────────────────────────
    const renderBonusAllocation = () => {
        const totalAmount = bonusAllocations.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
        const pendingCount = bonusAllocations.filter(b => b.status === 'pending' && !isBonusPeriodProcessed(b)).length;
        const processedCount = bonusAllocations.filter(b => isBonusPeriodProcessed(b)).length;

        return (
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Bonus Allocations
                    </h3>
                    <div className="flex gap-2">
                        <button
                            onClick={async () => {
                                if (selectedEmployee?.id) {
                                    setBonusLoading(true);
                                    try {
                                        const response = await api.get(`/api/compensation/employees/${selectedEmployee.id}/bonus-allocations`);
                                        if (response.data?.bonuses) setBonusAllocations(response.data.bonuses);
                                    } catch (e) { console.error(e); }
                                    finally { setBonusLoading(false); }
                                }
                            }}
                            className={`px-3 py-2 rounded flex items-center gap-2 ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                            title="Refresh"
                        >
                            <RefreshCw size={16} />
                        </button>
                        <button
                            onClick={openNewBonus}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                        >
                            <Plus size={16} />
                            Add Bonus
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                {bonusAllocations.length > 0 && (
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Allocated</div>
                            <div className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                                ₹{totalAmount.toLocaleString('en-IN')}
                            </div>
                        </div>
                        <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Pending</div>
                            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">{pendingCount}</div>
                        </div>
                        <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Processed</div>
                            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{processedCount}</div>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {bonusLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                ) : bonusAllocations.length === 0 ? (
                    /* Empty State */
                    <div className={`flex flex-col items-center justify-center py-12 rounded-lg border-2 border-dashed ${theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                        <DollarSign className="w-16 h-16 text-gray-400 mb-4" />
                        <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            No Bonus Allocations
                        </h4>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                            Award a bonus for a specific month to be included in payroll
                        </p>
                    </div>
                ) : (
                    /* Bonus List Table */
                    <div className="space-y-3">
                        {bonusAllocations.map((bonus) => (
                            <div
                                key={bonus.id}
                                className={`p-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h4 className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                                {bonus.bonus_type}
                                            </h4>
                                            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                                isBonusPeriodProcessed(bonus) ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                                bonus.status === 'cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                            }`}>
                                                {isBonusPeriodProcessed(bonus) ? 'Processed' : bonus.status === 'cancelled' ? 'Cancelled' : 'Pending'}
                                            </span>
                                        </div>
                                        <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                                            <span className="flex items-center gap-1"><Calendar size={14} /> {monthNames[bonus.month - 1]} {bonus.year}</span>
                                            {bonus.reason && <span>Reason: {bonus.reason}</span>}
                                        </div>
                                        {(bonus.processed_at || isBonusPeriodProcessed(bonus)) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                {bonus.processed_at
                                                    ? `Processed on ${new Date(bonus.processed_at).toLocaleDateString()}`
                                                    : `Payroll for ${monthNames[bonus.month - 1]} ${bonus.year} has been processed`
                                                }
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                                            ₹{parseFloat(bonus.amount).toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                </div>

                                {/* Actions - only for pending and not in a processed payroll period */}
                                {bonus.status === 'pending' && !isBonusPeriodProcessed(bonus) && (
                                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                                        <button
                                            onClick={() => openEditBonus(bonus)}
                                            className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 rounded hover:bg-blue-200 dark:hover:bg-blue-800 flex items-center gap-1"
                                        >
                                            <Edit3 size={14} /> Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteBonus(bonus.id)}
                                            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-800 flex items-center gap-1"
                                        >
                                            <Trash2 size={14} /> Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Add/Edit Bonus Modal */}
                {showBonusModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <div className={`w-full max-w-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl p-6`}>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {editingBonus ? 'Edit Bonus Allocation' : 'Add Bonus Allocation'}
                                </h3>
                                <button
                                    onClick={() => { setShowBonusModal(false); setEditingBonus(null); }}
                                    className={`p-1 rounded ${theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Bonus Type */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Bonus Type</label>
                                    <select
                                        value={bonusFormData.bonusType}
                                        onChange={(e) => setBonusFormData(prev => ({ ...prev, bonusType: e.target.value }))}
                                        className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                                    >
                                        {bonusTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Amount */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Amount (₹)</label>
                                    <input
                                        type="number"
                                        value={bonusFormData.amount}
                                        onChange={(e) => setBonusFormData(prev => ({ ...prev, amount: e.target.value }))}
                                        placeholder="Enter bonus amount"
                                        className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                                    />
                                </div>

                                {/* Month and Year */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Month</label>
                                        <select
                                            value={bonusFormData.month}
                                            onChange={(e) => setBonusFormData(prev => ({ ...prev, month: e.target.value }))}
                                            className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                                        >
                                            {monthNames.map((m, i) => (
                                                <option key={i} value={i + 1}>{m}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Year</label>
                                        <select
                                            value={bonusFormData.year}
                                            onChange={(e) => setBonusFormData(prev => ({ ...prev, year: e.target.value }))}
                                            className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                                        >
                                            {[2024, 2025, 2026, 2027].map(y => (
                                                <option key={y} value={y}>{y}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Reason */}
                                <div>
                                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Reason (Optional)</label>
                                    <textarea
                                        value={bonusFormData.reason}
                                        onChange={(e) => setBonusFormData(prev => ({ ...prev, reason: e.target.value }))}
                                        placeholder="Reason for the bonus"
                                        rows={3}
                                        className={`w-full px-3 py-2 rounded-lg border ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:outline-none`}
                                    />
                                </div>
                            </div>

                            {/* Modal Actions */}
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={() => { setShowBonusModal(false); setEditingBonus(null); }}
                                    className={`px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={editingBonus ? handleUpdateBonus : handleCreateBonus}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                                    disabled={bonusActionLoading || !bonusFormData.amount}
                                >
                                    {bonusActionLoading && <Loader2 size={16} className="animate-spin" />}
                                    {editingBonus ? 'Update Bonus' : 'Add Bonus'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderSectionContent = (section) => {
        switch(section) {
            case 'PAY PACKAGE':
                return renderPayPackage();
            case 'SALARY REVISIONS':
                return renderSalaryRevisions();
            case 'BONUS ALLOCATION':
                return renderBonusAllocation();
            case 'CTC PRORATION':
                return renderCTCProration();
            case 'FLEXI COMPONENTS':
                return renderFlexiComponents();
            case 'IT DECLARATION':
                return renderITDeclaration();
            case 'TAX SHEET':
                return renderTaxSheet();
            case 'PAY SLIPS':
                return renderPaySlips();
            case 'OFF CYCLE PAY SLIPS':
                return renderOffCyclePaySlips();
            case 'EXTRA PAYMENTS':
                return renderExtraPayments();
            case 'LOANS':
                return renderLoans();
            case 'ADVANCES / EXTRA DEDUCTIONS':
                return renderAdvances();
            case 'HOLD SALARY / REPAYMENT':
                return renderHoldSalary();
            case 'PERQUISITES':
                return renderPerquisites();
            default:
                return null;
        }
    };

    const renderITDeclaration = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">IT Declaration for Financial Year {selectedYear}</h3>
            <div className="space-y-4">
                <div className="flex gap-2 mb-4">
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm">OPEN FOR POI</span>
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">TAX REGIME</span>
                </div>
                <div className="bg-white p-4 border rounded">
                    <p className="text-sm text-gray-600">IT Declaration content and forms will appear here</p>
                </div>
            </div>
        </div>
    );

    const renderTaxSheet = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Tax Sheet</h3>
            <div className="bg-white p-4 border rounded">
                <p className="text-sm text-gray-600">Tax calculation details will appear here</p>
            </div>
        </div>
    );

    // Handle payslip download
    const handleDownloadPayslip = async (paySlip) => {
        // Always get fresh URL from backend to handle expired URLs
        try {
            await payrollService.downloadStoredPayslip(paySlip.id, paySlip.fileName);
        } catch (error) {
            console.error('Error downloading payslip:', error);
            alert('Failed to download payslip. Please try again.');
        }
    };

    // Re-fetch this employee's payslips (shared by the Refresh button and the
    // per-row Regenerate action).
    const refreshEmployeePayslips = async () => {
        if (!selectedEmployee?.id) return;
        setPaySlipsLoading(true);
        try {
            const response = await api.get(`/api/payslips/employee/${selectedEmployee.id}`);
            if (response.data.success && response.data.payslips) {
                const transformedPayslips = response.data.payslips.map(payslip => ({
                    id: payslip.id,
                    month: payslip.period_name || `${getMonthName(payslip.month)} ${payslip.year}`,
                    totalWorkDays: payslip.total_working_days || 0,
                    gross: payslip.gross_salary || 0,
                    deductions: payslip.total_deductions || 0,
                    tds: 0,
                    net: payslip.net_salary || 0,
                    status: payslip.status === 'locked' ? 'Released' : payslip.status || 'Pending',
                    pdfUrl: payslip.pdf_url,
                    fileName: payslip.file_name,
                    year: payslip.year,
                    monthNum: payslip.month,
                    salaryBreakdown: payslip.salary_breakdown || { earnings: [], deductions: [] },
                    daysWorked: payslip.days_worked || 0,
                    lopDays: payslip.lop_days || 0
                }));
                setPaySlipsData(transformedPayslips);
            }
        } catch (error) {
            console.error('Error refreshing payslips:', error);
        } finally {
            setPaySlipsLoading(false);
        }
    };

    // Regenerate a single candidate's payslip for one month.
    //
    // We recompute the DEDUCTIONS from the same state-based rule the payroll
    // Detailed Calculation uses (getProfessionalTax on the company state), so the
    // payslip always reflects correct statutory data — not whatever stale value
    // was baked in. The month's EARNINGS are preserved from the stored payslip,
    // which already encode that month's attendance/proration, so the gross never
    // shifts. The backend persists the result and re-issues the PDF.
    const handleRegeneratePayslip = async (paySlip) => {
        if (!selectedEmployee?.id || !paySlip?.monthNum || !paySlip?.year) {
            alert('Cannot regenerate: missing period information for this payslip.');
            return;
        }
        setRegeneratingPayslipId(paySlip.id);
        try {
            const breakdown = paySlip.salaryBreakdown || { earnings: [], deductions: [] };
            const earnings = Array.isArray(breakdown.earnings) ? breakdown.earnings : [];
            const storedDeductions = Array.isArray(breakdown.deductions) ? breakdown.deductions : [];
            const gross = Number(paySlip.gross) || earnings.reduce((s, e) => s + Number(e.amount || 0), 0);

            // Recompute Professional Tax from the company state (same source the
            // payroll Detailed Calculation uses), then rebuild the deductions list:
            // keep every non-PT deduction as-is and replace PT with the fresh value.
            // Resolve the state freshly if companyConfig hasn't loaded yet, so PT is
            // never computed against the wrong (default) state.
            let companyState = companyConfig.state;
            if (!companyState) {
                try {
                    const resp = await api.get('/api/company/settings');
                    const settings = resp.data?.data || resp.data;
                    companyState = settings?.address?.state || settings?.state || 'Maharashtra';
                } catch {
                    companyState = 'Maharashtra';
                }
            }
            const recomputedPT = getProfessionalTax(companyState, gross);
            const deductions = storedDeductions.filter(d => !/professional\s*tax/i.test(d.name || ''));
            if (recomputedPT > 0) {
                deductions.push({ name: 'Professional Tax', amount: Math.round(recomputedPT * 100) / 100 });
            }

            const totalDeductions = deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
            const net = gross - totalDeductions;

            const result = await payrollService.regenerateSinglePayslip({
                employeeId: selectedEmployee.id,
                month: paySlip.monthNum,
                year: paySlip.year,
                earnings,
                deductions,
                gross,
                totalDeductions,
                net,
                totalWorkingDays: paySlip.totalWorkDays || 0,
                daysWorked: paySlip.daysWorked || 0,
                lopDays: paySlip.lopDays || 0
            });

            const fmt = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;
            const beforeNet = result?.before?.net;
            const afterNet = result?.after?.net;
            if (beforeNet != null && afterNet != null && Number(beforeNet) !== Number(afterNet)) {
                alert(`Payslip regenerated for ${paySlip.month}.\nNet pay: ${fmt(beforeNet)} → ${fmt(afterNet)}`);
            } else {
                alert(`Payslip regenerated for ${paySlip.month}.`);
            }
            await refreshEmployeePayslips();
        } catch (error) {
            console.error('Error regenerating payslip:', error);
            alert(`Failed to regenerate payslip: ${error.response?.data?.error || error.message}`);
        } finally {
            setRegeneratingPayslipId(null);
        }
    };

    const renderPaySlips = () => (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Pay Slips {selectedEmployee ? `for ${selectedEmployee.first_name} ${selectedEmployee.last_name}` : ''}
                </h3>
                {paySlipsData.length > 0 && (
                    <button
                        onClick={refreshEmployeePayslips}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                            theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                    >
                        <RefreshCw size={14} className={paySlipsLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                )}
            </div>

            {/* Loading State */}
            {paySlipsLoading ? (
                <div className={`flex flex-col items-center justify-center py-16 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                    <Loader2 className="w-8 h-8 animate-spin mb-4" />
                    <p>Loading payslips...</p>
                </div>
            ) : paySlipsError ? (
                /* Error State */
                <div className={`flex flex-col items-center justify-center py-16 px-6 rounded-lg border-2 border-dashed ${
                    theme === 'dark' ? 'border-red-800 bg-red-900/20' : 'border-red-300 bg-red-50'
                }`}>
                    <div className={`p-4 rounded-full mb-4 ${
                        theme === 'dark' ? 'bg-red-900/50' : 'bg-red-100'
                    }`}>
                        <X className={`w-12 h-12 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`} />
                    </div>
                    <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                        Error Loading Payslips
                    </h4>
                    <p className={`text-sm text-center max-w-md ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                        {paySlipsError}
                    </p>
                </div>
            ) : paySlipsData.length === 0 ? (
                /* Empty State */
                <div className={`flex flex-col items-center justify-center py-16 px-6 rounded-lg border-2 border-dashed ${
                    theme === 'dark' ? 'border-gray-600 bg-gray-800/50' : 'border-gray-300 bg-gray-50'
                }`}>
                    <div className={`p-4 rounded-full mb-4 ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                        <FileText className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                    <h4 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        No Pay Slips Available
                    </h4>
                    <p className={`text-sm text-center max-w-md ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                        Pay slips for this employee will appear here once payroll has been processed and locked.
                        Process payroll from the Payroll Run section to generate pay slips.
                    </p>
                </div>
            ) : (
                /* Pay Slips Table */
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                        <tr className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {showPaySlipCheckboxes && (
                                <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>
                                    <input
                                        type="checkbox"
                                        checked={selectedPaySlips.length === paySlipsData.length && paySlipsData.length > 0}
                                        onChange={handleSelectAllPaySlips}
                                        className="w-4 h-4"
                                    />
                                </th>
                            )}
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Month</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Total Work Days</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Gross</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Deductions</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>TDS</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Net</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Status</th>
                            <th className={`border p-3 text-left ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'}`}>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paySlipsData.map((paySlip) => (
                            <tr key={paySlip.id} className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                {showPaySlipCheckboxes && (
                                    <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>
                                        <input
                                            type="checkbox"
                                            checked={selectedPaySlips.includes(paySlip.id)}
                                            onChange={() => handleSelectPaySlip(paySlip.id)}
                                            className="w-4 h-4"
                                        />
                                    </td>
                                )}
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{paySlip.month}</td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{paySlip.totalWorkDays}</td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(paySlip.gross)}</td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(paySlip.deductions)}</td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-900'}`}>{formatCurrencyValue(paySlip.tds)}</td>
                                <td className={`border p-3 font-semibold text-green-600 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>{formatCurrencyValue(paySlip.net)}</td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                                    <span className={`px-2 py-1 rounded text-sm ${
                                        paySlip.status === 'Released'
                                            ? 'bg-green-100 text-green-800'
                                            : paySlip.status === 'locked'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {paySlip.status}
                                    </span>
                                </td>
                                <td className={`border p-3 ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                                    <div className="flex items-center gap-3">
                                        {paySlip.pdfUrl ? (
                                            <button
                                                onClick={() => handleDownloadPayslip(paySlip)}
                                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                            >
                                                <Download size={16} />
                                                Download
                                            </button>
                                        ) : (
                                            <span className="text-gray-400 text-sm">Not available</span>
                                        )}
                                        <button
                                            onClick={() => handleRegeneratePayslip(paySlip)}
                                            disabled={regeneratingPayslipId === paySlip.id}
                                            title="Recompute and re-issue this payslip"
                                            className="text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RefreshCw size={16} className={regeneratingPayslipId === paySlip.id ? 'animate-spin' : ''} />
                                            {regeneratingPayslipId === paySlip.id ? 'Regenerating…' : 'Regenerate'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    const renderOffCyclePaySlips = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Off Cycle Pay Slips</h3>
            <div className="bg-white p-4 border rounded">
                <p className="text-sm text-gray-600">Off cycle payment details will appear here</p>
            </div>
        </div>
    );

    const renderExtraPayments = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Extra Payments for {selectedYear}</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-3 text-left">Payment Type</th>
                        <th className="border p-3 text-left">Amount</th>
                        <th className="border p-3 text-left">Frequency</th>
                        <th className="border p-3 text-left">Date</th>
                        <th className="border p-3 text-left">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr className="hover:bg-gray-50">
                        <td className="border p-3">Bonus</td>
                        <td className="border p-3">{formatCurrencyValue(50000)}</td>
                        <td className="border p-3">One-time</td>
                        <td className="border p-3">June 2023</td>
                        <td className="border p-3">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Processed</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderLoans = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Loans</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-3 text-left">Loan Type</th>
                        <th className="border p-3 text-left">Amount</th>
                        <th className="border p-3 text-left">EMI</th>
                        <th className="border p-3 text-left">Tenure</th>
                        <th className="border p-3 text-left">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr className="hover:bg-gray-50">
                        <td className="border p-3">Personal Loan</td>
                        <td className="border p-3">{formatCurrencyValue(200000)}</td>
                        <td className="border p-3">{formatCurrencyValue(8500)}</td>
                        <td className="border p-3">24 months</td>
                        <td className="border p-3">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">Active</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderAdvances = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Advances / Extra Deductions</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-3 text-left">Type</th>
                        <th className="border p-3 text-left">Amount</th>
                        <th className="border p-3 text-left">Monthly Deduction</th>
                        <th className="border p-3 text-left">Period</th>
                        <th className="border p-3 text-left">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr className="hover:bg-gray-50">
                        <td className="border p-3">Travel Advance</td>
                        <td className="border p-3">{formatCurrencyValue(24000)}</td>
                        <td className="border p-3">{formatCurrencyValue(2000)}</td>
                        <td className="border p-3">12 months</td>
                        <td className="border p-3">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">In Progress</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderHoldSalary = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Hold Salary / Repayment</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-3 text-left">Hold Period</th>
                        <th className="border p-3 text-left">Amount</th>
                        <th className="border p-3 text-left">Reason</th>
                        <th className="border p-3 text-left">Release Date</th>
                        <th className="border p-3 text-left">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr className="hover:bg-gray-50">
                        <td className="border p-3">Nov 2023</td>
                        <td className="border p-3">{formatCurrencyValue(0)}</td>
                        <td className="border p-3">Process in F&F</td>
                        <td className="border p-3">Dec 2023</td>
                        <td className="border p-3">
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">Pending</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderPerquisites = () => (
        <div className="p-6">
            <h3 className="text-xl font-semibold mb-4">Perquisites</h3>
            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                    <tr className="bg-gray-100">
                        <th className="border p-3 text-left">Type</th>
                        <th className="border p-3 text-left">Description</th>
                        <th className="border p-3 text-left">Amount</th>
                        <th className="border p-3 text-left">Period</th>
                        <th className="border p-3 text-left">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr className="hover:bg-gray-50">
                        <td className="border p-3">Car & Driver</td>
                        <td className="border p-3">Company Car with Driver</td>
                        <td className="border p-3">{formatCurrencyValue(25000)}</td>
                        <td className="border p-3">Apr 2023 - Mar 2024</td>
                        <td className="border p-3">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Active</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Main Payroll View
    return (
        <div className="flex-1 p-4">
            {/* Header Controls */}
            <div className="mb-4">
                <button
                    onClick={() => onBack ? onBack() : setSelectedEmployee(null)}
                    className="mb-3 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 flex items-center gap-2"
                >
                    <ChevronLeft size={20} />
                    Back to Employee Selection
                </button>

                {/* Employee Info and Controls */}
                <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm p-4 mb-4`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img
                                src={selectedEmployee.photo}
                                alt={selectedEmployee.name}
                                className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                                <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{selectedEmployee.name}</p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{selectedEmployee.designation} • {selectedEmployee.id}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <label className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Salary Version:</label>
                                <select
                                    value={selectedRevisionId}
                                    onChange={(e) => handleRevisionSelect(e.target.value)}
                                    className={`px-3 py-1.5 border rounded text-sm min-w-[200px] ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    } ${activeTab === 'CTC PRORATION' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    disabled={activeTab === 'CTC PRORATION'}
                                >
                                    {revisionOptions.map(option => (
                                        <option key={option.id} value={option.id}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <DollarSign size={16} className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                                <select
                                    value={globalCurrency}
                                    onChange={(e) => setGlobalCurrency(e.target.value)}
                                    className={`px-2 py-1 border rounded text-sm ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                >
                                    {Object.entries(currencyRates).map(([code, info]) => (
                                        <option key={code} value={code}>{code} ({info.symbol})</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Grid Layout for Section Tabs */}
                <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm p-4 mb-4`}>
                    <div className="space-y-2">
                        {tabSections.map((row, rowIndex) => (
                            <div key={rowIndex} className="grid grid-cols-6 gap-2">
                                {row.map((section) => (
                                    <button
                                        key={section}
                                        onClick={() => {
                                            setActiveTab(section);
                                            if (section === 'BONUS ALLOCATION') setBonusRefreshKey(k => k + 1);
                                        }}
                                        className={`p-2 rounded text-xs font-medium transition-all ${
                                            activeTab === section
                                                ? 'bg-blue-600 text-white'
                                                : theme === 'dark'
                                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {section}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Active Section Content */}
                <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-sm`}>
                    {renderSectionContent(activeTab)}
                </div>
            </div>

            {/* Modals */}

            {/* Add Financial Year Modal */}
            {showAddYearModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-96">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Add Financial Year</h3>
                            <button onClick={() => setShowAddYearModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Enter Financial Year (e.g., 2024-25)</label>
                                <input
                                    type="text"
                                    value={newYear}
                                    onChange={(e) => setNewYear(e.target.value)}
                                    placeholder="YYYY-YY"
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowAddYearModal(false)}
                                className="px-4 py-2 border rounded hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddYear}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Add Year
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Flexi Status Modal */}
            {showFlexiStatusModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-96">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Edit Flexi Payroll Declaration Access</h3>
                            <button onClick={() => setShowFlexiStatusModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Select Status</label>
                                <select
                                    value={flexiStatus.status}
                                    onChange={(e) => setFlexiStatus({...flexiStatus, status: e.target.value})}
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="OPEN">OPEN</option>
                                    <option value="CLOSED">CLOSED</option>
                                    <option value="Under Review">Under Review</option>
                                    <option value="Pending">Pending</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Manage date</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs text-gray-600">From Date</label>
                                        <input
                                            type="date"
                                            value={flexiStatus.fromDate}
                                            onChange={(e) => {
                                                const newFromDate = e.target.value;
                                                if (new Date(newFromDate) <= new Date(flexiStatus.upToDate)) {
                                                    setFlexiStatus({...flexiStatus, fromDate: newFromDate});
                                                } else {
                                                    alert('From Date must be before or equal to Upto Date');
                                                }
                                            }}
                                            className="w-full p-2 border rounded"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-600">
                                            {(flexiStatus.status === 'Pending' || flexiStatus.status === 'Under Review') ? 'To Date' : 'Upto Date'}
                                        </label>
                                        {(flexiStatus.status === 'Pending' || flexiStatus.status === 'Under Review') ? (
                                            <input
                                                type="text"
                                                value="NA"
                                                disabled
                                                className="w-full p-2 border rounded bg-gray-100 text-gray-500"
                                            />
                                        ) : (
                                            <input
                                                type="date"
                                                value={flexiStatus.upToDate}
                                                onChange={(e) => {
                                                    const newUpToDate = e.target.value;
                                                    if (new Date(newUpToDate) >= new Date(flexiStatus.fromDate)) {
                                                        setFlexiStatus({...flexiStatus, upToDate: newUpToDate});
                                                    } else {
                                                        alert('Upto Date must be after or equal to From Date');
                                                    }
                                                }}
                                                className="w-full p-2 border rounded"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowFlexiStatusModal(false)}
                                className="px-4 py-2 border rounded hover:bg-gray-50"
                            >
                                CANCEL
                            </button>
                            <button
                                onClick={() => {
                                    setShowFlexiStatusModal(false);
                                    // Add new entry to history
                                    const newEntry = {
                                        status: flexiStatus.status,
                                        fromDate: flexiStatus.fromDate,
                                        toDate: (flexiStatus.status === 'Pending' || flexiStatus.status === 'Under Review') ? 'NA' : flexiStatus.upToDate,
                                        changedOn: new Date().toISOString().split('T')[0],
                                        changedBy: 'Current User'
                                    };
                                    setFlexiHistory([newEntry, ...flexiHistory]);
                                    alert('Status updated successfully!');
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                SUBMIT
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Flexi History Modal */}
            {showFlexiHistoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-3/4 max-w-4xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Flexi Status Change History</h3>
                            <button onClick={() => setShowFlexiHistoryModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-3 text-left">Status</th>
                                    <th className="border p-3 text-left">From Date</th>
                                    <th className="border p-3 text-left">To Date</th>
                                    <th className="border p-3 text-left">Changed On</th>
                                    <th className="border p-3 text-left">Changed By</th>
                                </tr>
                                </thead>
                                <tbody>
                                {flexiHistory.map((entry, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="border p-3">
                        <span className={`px-2 py-1 rounded text-sm ${getStatusColor(entry.status)}`}>
                          {entry.status}
                        </span>
                                        </td>
                                        <td className="border p-3">{formatDateDDMMYYYY(entry.fromDate)}</td>
                                        <td className="border p-3">
                                            {entry.toDate === 'NA'
                                                ? <span className="text-gray-500">NA</span>
                                                : formatDateDDMMYYYY(entry.toDate)}
                                        </td>
                                        <td className="border p-3">{formatDateDDMMYYYY(entry.changedOn)}</td>
                                        <td className="border p-3">{entry.changedBy}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => setShowFlexiHistoryModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* CTC Proration Modal */}
            {showCTCProrationModal && selectedProrationRow && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-4/5 max-w-4xl max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">
                                CTC Proration Details ({selectedProrationRow.startDate} - {selectedProrationRow.endDate})
                            </h3>
                            <button onClick={() => setShowCTCProrationModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-3 text-left">Month</th>
                                    <th className="border p-3 text-left">Working Days</th>
                                    <th className="border p-3 text-left">Absence (Days)</th>
                                    <th className="border p-3 text-left">CTC Days</th>
                                    <th className="border p-3 text-left">Prorated Amount</th>
                                    <th className="border p-3 text-left">Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {selectedProrationRow.months.map((month, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="border p-3">{month.month}</td>
                                        <td className="border p-3">{month.workingDays}</td>
                                        <td className="border p-3">{month.absence}</td>
                                        <td className="border p-3">{month.ctcDays}</td>
                                        <td className="border p-3">{formatCurrencyValue(month.amount)}</td>
                                        <td className="border p-3">
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Processed</span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-4 p-3 bg-gray-50 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>Total for Period:</strong> {formatCurrencyValue(selectedProrationRow.months.reduce((sum, m) => sum + m.amount, 0))}
                            </p>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => setShowCTCProrationModal(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Variable Pay Modal */}
            {showAddVariablePayModal && editingPayPackage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-96">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Add/Edit Variable Pay</h3>
                            <button onClick={() => setShowAddVariablePayModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Variable Pay Amount ({currencyRates[globalCurrency].symbol})</label>
                                <input
                                    type="number"
                                    value={editingPayPackage.variablePay}
                                    onChange={(e) => setEditingPayPackage({...editingPayPackage, variablePay: e.target.value})}
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="p-3 bg-blue-50 rounded">
                                <p className="text-sm text-blue-700">Variable pay is typically performance-based and paid annually or quarterly.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowAddVariablePayModal(false)}
                                className="px-4 py-2 border rounded hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const updatedData = [...payPackageData];
                                    updatedData[editingPayPackage.index].variablePay = editingPayPackage.variablePay;
                                    setPayPackageData(updatedData);
                                    setShowAddVariablePayModal(false);
                                    setEditingPayPackage(null);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Flexi Component Modal */}
            {showAddFlexiModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} p-6 rounded-lg w-96`}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Add Customized Flexi Component</h3>
                            <button
                                onClick={() => {
                                    setNewFlexiComponent({
                                        name: '',
                                        lowerLimit: '',
                                        upperLimit: '',
                                        currency: globalCurrency,
                                        value: 0
                                    });
                                    setShowAddFlexiModal(false);
                                }}
                                className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Flexi Component Name</label>
                                <input
                                    type="text"
                                    value={newFlexiComponent.name}
                                    onChange={(e) => setNewFlexiComponent({...newFlexiComponent, name: e.target.value})}
                                    placeholder="Enter component name"
                                    className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                    }`}
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Range Limits</label>
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newFlexiComponent.lowerLimit}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                setNewFlexiComponent({...newFlexiComponent, lowerLimit: value});
                                            }
                                        }}
                                        placeholder="Lower limit"
                                        min="0"
                                        className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                        }`}
                                    />
                                    <span className={`py-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>-</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newFlexiComponent.upperLimit}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                                setNewFlexiComponent({...newFlexiComponent, upperLimit: value});
                                            }
                                        }}
                                        placeholder="Upper limit"
                                        min="0"
                                        className={`w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                        }`}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Currency</label>
                                <div className={`w-full p-2 border rounded ${
                                    theme === 'dark'
                                        ? 'bg-gray-600 border-gray-500 text-gray-400'
                                        : 'bg-gray-100 border-gray-300 text-gray-500'
                                }`}>
                                    {globalCurrency} ({currencyRates[globalCurrency].symbol}) - Default from payroll settings
                                </div>
                                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Currency is automatically set to match the payroll currency selection
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => {
                                    setShowAddFlexiModal(false);
                                    setNewFlexiComponent({
                                        name: '',
                                        lowerLimit: '',
                                        upperLimit: '',
                                        currency: 'INR',
                                        value: 0
                                    });
                                }}
                                className="px-4 py-2 border rounded hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddFlexiComponent}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Request Dismiss Modal */}
            {showRequestModal && selectedRequest && requestAction === 'dismiss' && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className={`max-w-md w-full mx-4 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    Dismiss Flexi Component Request
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowRequestModal(false);
                                        setSelectedRequest(null);
                                        setDismissReason('');
                                        setRequestAction('');
                                    }}
                                    className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                                >
                                    <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-600'} />
                                </button>
                            </div>

                            <div className="mb-4">
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Employee: {selectedRequest.employeeName}
                                </p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Request: {selectedRequest.title}
                                </p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                    Amount: {selectedRequest.currency} {selectedRequest.amount.toLocaleString()}
                                </p>
                            </div>

                            <div className="mb-4">
                                <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Reason for Dismissal *
                                </label>
                                <textarea
                                    value={dismissReason}
                                    onChange={(e) => setDismissReason(e.target.value)}
                                    placeholder="Please provide a detailed reason for dismissing this request..."
                                    rows={4}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 placeholder-gray-500'
                                    }`}
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowRequestModal(false);
                                        setSelectedRequest(null);
                                        setDismissReason('');
                                        setRequestAction('');
                                    }}
                                    className={`px-4 py-2 text-sm rounded-lg border ${
                                        theme === 'dark'
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDismissRequest}
                                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Dismiss Request
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Component Selector Modal */}
            {showComponentSelector && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                        onClick={() => setShowComponentSelector(false)}
                    />
                    <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Salary Component</h3>
                            <button
                                onClick={() => setShowComponentSelector(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Custom Component */}
                        <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                            <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-3">Custom Component</h4>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={customComponentName}
                                    onChange={(e) => setCustomComponentName(e.target.value)}
                                    placeholder="Enter component name"
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <select
                                    value={componentType}
                                    onChange={(e) => setComponentType(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="earning">Earning</option>
                                    <option value="deduction">Deduction</option>
                                </select>
                                <button
                                    onClick={handleAddCustomComponent}
                                    disabled={!customComponentName.trim()}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                >
                                    Add
                                </button>
                            </div>
                        </div>

                        {/* Predefined Earnings */}
                        <div className="mb-4">
                            <h4 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">EARNINGS</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {salaryComponentTemplates.earnings.map((template, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleAddComponent(template)}
                                        className="text-left px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 text-sm text-gray-900 dark:text-white"
                                    >
                                        {template.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Predefined Deductions */}
                        <div>
                            <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-2">DEDUCTIONS</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {salaryComponentTemplates.deductions.map((template, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleAddComponent(template)}
                                        className="text-left px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-sm text-gray-900 dark:text-white"
                                    >
                                        {template.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PayrollFlowWithEmployeeSelection;
